import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import client, { apiErr, API } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PUBLIC_LANGS, LANGS, HREFLANG } from "../i18n";
import { Button, Input, Card, Modal, Badge } from "../components/ui";
import { useBranding } from "../components/BrandFooter";
import ProgramReview from "../components/ProgramReview";
import SolutionsInfographic from "../components/SolutionsInfographic";
import { BookOpen, Info, Mail, Phone, Lightbulb, Newspaper, Twitter, Linkedin, Link2, Check, ArrowRight, Apple, Play, ShoppingBag, Lock, Layers, Loader2, Languages, Star, Volume2, Download, BadgeCheck, MessageSquare } from "lucide-react";

// English source of record for on-demand localized marketing chrome/copy (mirrors backend LANDING_STRINGS).
const LANDING_EN = {
  nav_solutions: "Solutions", nav_blog: "Blog", nav_about: "About", nav_contact: "Contact",
  nav_home: "Home", nav_sitemap: "Site map",
  home_lead: "Bring your family, holdings, currencies, and benefits together in one private, credible place \u2014 powerful for experts, welcoming for everyone.",
  home_explore: "Explore the Solutions",
  home_tagline: "your Full Option Family Office",
  home_unified: "unified by the General Administration & Master Enabler",
  blog_subtitle: "Family Office Theory and Practice meet the FopFo™ Suite",
  blog_title_short: "Short Blog (Apps)",
  blog_title_long: "Long Blog (SaaS)",
  rights_reserved: "All Rights Reserved",
  sign_in: "Sign in", footer_privacy: "Privacy Policy", footer_terms: "Terms of Use", footer_copyright: "Copyright Policy", footer_cookies: "Cookies Policy", footer_backup: "Backup & Recovery Policy", footer_specs: "Specifications", footer_eula: "EULA", footer_solutions: "Solutions",
  solutions_title: "Solutions", make_payment: "Make a payment", card_learn_more: "Learn more",
  play_tour: "Play the guided tour", play_tour_sub: "Narrated · all 15 languages · ~2 min",
  download_diagram: "Download diagram",
  pay_secure_title: "Secure payment", pay_purchasing: "Purchasing:",
  pay_intro: "Pay by card via Stripe, or have a secure link emailed to you.",
  pay_sales_label: "Product Sale (per app; applicable tax added at checkout)",
  pay_service_label: "Annual Service Charge ($7.50/mo billed annually; applicable tax added at checkout)",
  pay_activation_label: "Activation Payment", pay_amount_ph: "Amount (USD)",
  pay_opt_suite: "All four — Full Suite", pay_full_suite: "Full Suite", pay_single_app: "Single application",
  pay_include_prefix: "Include the required", pay_service_charge: "Annual Service Charge",
  pay_yr_account: "/yr, per account", pay_total: "Total:", pay_plus_tax: "+ tax at checkout",
  pay_covers_title: "This annual charge covers:",
  pay_cover_1: "Updates and patches", pay_cover_2: "Newsletter subscription",
  pay_cover_3: "Customer Service — profile maintenance", pay_cover_4: "Customer Service — usage guidance",
  pay_cover_5: "Customer Service — troubleshooting",
  pay_now: "Pay now", pay_email_link: "Email me a link",
  pay_warn_title: "Annual Service Charge required",
  pay_warn_body_a: "Every purchase includes the mandatory",
  pay_warn_body_b: "Please tick the box to include it before you continue.",
  pay_go_back: "Go back", pay_add_it: "Add it",
  activation_caveat: "Use only as directed by Customer Service.",
  buy_application: "Buy Application", get_full_suite: "Get the Full Suite", watch_demo: "Watch Demo",
  demo_narrated: "Narrated", demo_narrated_note: "Voice-narrated walkthrough (15 languages coming)",
  hook_family_tree: "Track every relative, branch, and beneficiary across generations.",
  hook_holdings: "Every asset, rental, and investment across jurisdictions in one view.",
  hook_currency: "Physical, virtual, and crypto accounts in a single trustworthy ledger.",
  hook_benefits: "Calm, accurate benefit schedules and professional disbursements.",
  name_family_tree: "Family Tree Application",
  name_holdings: "Holdings Management Application",
  name_currency: "Currency Management Application",
  name_benefits: "Benefits Management Application",
  pn_title: "FopFo\u2122 \u2014 Please Note",
  pn_intro: "How your purchase unlocks across devices \u2014 **and why you never pay twice**:",
  pn_b1: "Buying on **this website** gives you our **direct web discount** plus a **20-character activation code** tied to your email and mobile number. You can start using your application right away in your **secure online (hosted) instance**.",
  pn_b2: "When the native apps arrive, that **same code unlocks them too**. The **desktop app (Windows & macOS) will be a free download directly from us**, and the **mobile apps will be free downloads on the App Store & Google Play** \u2014 you simply install and sign in (or enter your code) to unlock. **There is no second charge.**",
  pn_b3: "Because the store apps are **free to download** and unlock through your account, the stores don't ask you to pay again. If you ever choose to **buy inside a mobile app instead**, that goes through the store's own billing at the **standard (undiscounted) price** \u2014 so purchasing here first is how you keep the discount.",
  pn_foot: "Native desktop & mobile apps are on the way; today your purchase runs as a hosted online instance. Applicable tax is calculated at checkout based on your location.",
  about_title: "About GCA",
  about_p1: "GCA was formed in **2025 in Edmonton, Alberta, Canada**. As a \u201c_Creative Advisory_\u201d, we look at things with a hyper-critical eye, and are always searching for what will constitute that better mousetrap.",
  about_p2: "**Our mission**, is to help make managing your family and finances, more fun than frustrating.",
  about_p3: "**Our vision**, is to make delivering payments and benefits, more seamless than stressful; and the management of holdings and accounts, transactions, and charges, more visible than vexatious.",
  about_p4: "**Our values**, are discretion, accuracy, and care. We treat privacy as an architecture, not an afterthought. We hold ourselves to the highest standard of accuracy because people rely on the numbers. We design with genuine care for the people who buy, use, and value our products.",
  about_p5: "**Our goals**, in producing Solutions that are powerful for experts yet welcoming for everyone, are to build tools that unify the many moving parts of wealth into one single, credible picture. This will consistently make us the _GoTo_ partner that our clients can trust for generations to come.",
  about_p6: "To this end, we will keep expanding upon our initial offering of the Full Option Family Office (FopFo\u2122) suite, with additional thoughtful solutions that build upon the successes of the _Family Tree Application_ (Familigi\u2122), the _Holdings Management Application_ (BoxAlong\u2122), the _Currency Management Application_ (Currencida\u2122), and the _Benefits Management Application_ \u2013 also known as your Simple, Efficient Benefits Administration Application (SEBAA\u2122).",
  contact_title: "Contact us", contact_first: "First name", contact_last: "Last name",
  contact_email: "Your email", contact_subject: "Subject", contact_comment: "Comment",
  contact_receipt: "Email me a receipt / confirmation", contact_send: "Send message",
  subj_vendor: "Product or Service Vendor query", subj_employment: "Employment Query",
  subj_media: "Media and Publicity", subj_support: "Customer Support Callback",
  subj_sales: "Solution Sales query", subj_topic: "Topic Not Found", subj_payment: "Request Payment Link",
  subj_privacy: "PRIVACY QUERY",
  subj_saas_manager: "SAAS Manager", subj_saas_notices: "SAAS Notices",
  subj_saas_dispute: "SAAS Dispute", subj_saas_executive: "SAAS Executive",
  subj_children13: "Children under 13", subj_misuse: "Misuse or Misconduct",
  fm_title: "Feature Matrix", fm_intro: "One family. Every asset, currency and generation — in a single, auditable picture. Compare the suite side-by-side, then pick one, or unlock all four and see the whole estate at a glance.",
  fm_grp_common: "Common to all four applications", fm_grp_byapp: "Features specific to each application",
  fm_grp_best: "★ Best-in-class — what sets FopFo apart",
  fm_consolidated_networth: "Consolidated multi-currency net worth",
  fm_pe_analytics: "Private-fund IRR, MOIC, DPI & TVPI",
  fm_capital_calls: "Capital-call & distribution tracker",
  fm_exposure: "Exposure, liquidity & concentration analytics",
  fm_appraisals: "Per-asset FMV appraisal history",
  fm_beneficiary_map: "Family-tree → asset beneficiary mapping",
  fm_gedcom: "GEDCOM import / export / merge",
  fm_attachments: "Per-asset photo & document vault",
  fm_export: "One-click PDF / CSV / JSON export — you own your data",
  fm_autocat: "Rule-based auto-categorization on import",
  fm_audit: "Governed, timestamped audit trail",
  fm_rbac: "Role-based access + multi-factor sign-in",
  fm_i18n: "15 languages, including right-to-left Arabic",
  fm_reminders: "Automated reminders & scheduled statements",
  fm_encryption: "Field-level encryption at rest",
  fm_ownership: "Flat ownership pricing — never a % of your assets",
  fm_kt_label: "Key takeaway",
  fm_kt: "An integrated suite that links your family tree to every asset — in your currency and your language — is the most reliable way to keep the whole picture accurate, private and auditable across generations.",
  fm_proof_title: "What to insist on — and how to verify it",
  fm_proof_intro: "Good family-office software should prove these things live. Ask any vendor to demonstrate each one in your very first demo. If they can’t, keep looking.",
  fm_proof_1: "“Show me one net-worth number across every currency.” — We roll every holding and cash pool into your chosen base currency, live, with rates you govern.",
  fm_proof_2: "“Show me IRR, MOIC, DPI and TVPI on a private fund, from its own cash flows.” — We compute them from your capital calls, distributions and latest NAV.",
  fm_proof_3: "“Link a beneficiary to an asset and export a trustee-ready report.” — Familigi™ maps people to assets and exports to PDF/CSV in one click.",
  fm_proof_4: "“Let me export ALL of my data, right now, with no lock-in.” — CSV, JSON and GEDCOM export across every module — and flat pricing, never a slice of your wealth.",
  fm_proof_5: "“Do all of it in my language.” — 15 languages out of the box, including right-to-left Arabic.",
  fm_automation: "Automation Tools", fm_data_analysis: "Data Analysis", fm_data_viz: "Colorful interactive dashboards",
  fm_collab_software: "Collaboration Software", fm_team_collab: "Team Collaboration",
  fm_presentation: "Presentation", fm_photo_sharing: "Photo Sharing", fm_tree_sharing: "Family Tree Sharing",
  fm_orgmap: "Organizational Map Sharing", fm_investing: "Investing", fm_budgeting: "Budgeting",
  fm_asset_mgmt: "Asset Management", fm_treasury: "Treasury Management", fm_crypto: "Crypto Wallet",
  fm_online_banking: "Online Banking (record keeping only)", fm_financial_planning: "Financial Planning",
  fm_benefits_mgmt: "Benefits Management", fm_payroll: "Payroll Management", fm_savings: "Savings Management",
  fm_investing_mgmt: "Investing Management",
  fm_cta: "Get all four — save {pct}%", fm_cta_note: "One purchase unlocks the entire suite.",
  st_title: "Support tiers — flat pricing, no AUM fees",
  st_intro: "We charge a flat monthly fee for support and AI — never a percentage of your wealth. Your assets can grow tenfold; your fee doesn’t move.",
  st_no_aum: "No AUM-based pricing. No performance cut. No asset-value tax. Just a simple monthly plan you can change at any time.",
  st_col_plan: "Plan", st_col_price: "Monthly", st_col_parses: "AI statement parses / mo",
  st_col_questions: "“Ask your data” questions / mo", st_col_sla: "Targeted support response", st_col_best_for: "Best for",
  st_col_portal: "Principal / advisor portal & print branding",
  st_portal_base: "FopFo logo + family name",
  st_portal_custom: "+ your logo (secondary)",
  st_portal_osmium: "Your logo only + family name",
  st_diamond_for: "Everyday record-keeping & support",
  st_platinum_for: "Active families automating their statements",
  st_rhodium_for: "Multi-fund, multi-currency households",
  st_osmium_for: "Complex, high-volume family offices",
  st_included: "Included in every plan: software updates, the newsletter, and customer-service profile help.",
  st_footnote: "Prices in USD; applicable tax is added at checkout. A “parse” reads a PDF or image statement and pre-fills an entry for your review; a “question” answers in plain language from your own data. Diamond includes a small taste of questions — upgrade to unlock statement parsing.",
  st_addon: "Add-on",
  sc_discount_badge: "BEST VALUE · {pct}% OFF",
  sc_suite_desc: "All four applications — Familigi™, BoxAlong™, Currencida™ and SEBAA™ — unlocked together in a single purchase.",
  sc_one_time: "one-time", sc_web_discount: "web discount",
  sc_store_price: "…and otherwise USD {amount} in the App Store / Play.",
  sc_taxes: "Taxes calculated at checkout based on your location.",
  sc_service: "+ USD 90.00/yr per account (customer support, updates, and newsletter), due on purchase.",
  sc_full_suite: "Full Suite",
  fidux_subtitle: "A Newsletter on the Words, Ways, and World of Wealth",
  solutions_outro: "Meanwhile, our product pipeline is always humming, so more may come.  We like to announce things like Play Store/App Store releases, changes to Terms, and our major or notable events in the Blog or a Newsletter edition.  Subscribing is free, and a complimentary perk for paid-up clients, so sign-up or feel free to come back and see what's new, anytime.",
  nl_subscribe: "Subscribe", nl_unsubscribe: "Unsubscribe", nl_share: "Share an edition with a colleague →",
  nl_recent: "Recent editions", nl_delivered: "Delivered to subscribers by email",
  nl_backissue_hint: "Subscribers can have any past edition re-sent. Enter your subscriber email, then choose an edition below.",
  nl_backissue_email: "Your subscriber email", nl_email_me: "Email me this edition", nl_sending: "Sending…",
  nl_get_archive: "Get the full archive",
  nl_full_name: "Full name", nl_email: "Email",
  nl_access1: "Access to FIDUX is by subscription only. There is no cost to subscribe, and access is included as a courtesy for our active customers in good standing.",
  nl_access2: "Each issue is delivered by email. You can unsubscribe at any time from the link in every issue, or below.",
  auto_translated_notice: "Auto-translated. Full articles are translated when you open them.",
  seo_home_desc: "Manage your family, holdings, currency and benefits in one unified, private, credible family-office suite.",
  seo_solutions_desc: "The FopFo suite: Family Tree, Holdings, Currency and Benefits management for high-net-worth operators and beyond.",
  seo_about_desc: "GCA: our values, mission and goals. Formed in 2025 in Edmonton, Alberta, Canada.",
  seo_contact_desc: "Contact GCA. Vendor, employment, media, support, sales, and payment requests.",
  seo_blog_desc: "Insights on family office software, wealth management, security, privacy and scalability.",
  seo_fidux_desc: "Subscribe to FIDUX™ — A Newsletter on the Words, Ways, and World of Wealth.",
  // program reviews widget
  rv_no_reviews: "No reviews yet",
  rv_reviews_word: "reviews",
  rv_across: "across",
  rv_verified_from: "from verified customers",
  rv_write: "Write a review",
  rv_read: "Read reviews",
  rv_hide: "Hide reviews",
  rv_your_rating: "Your rating (1–7):",
  rv_name_ph: "Your name (optional)",
  rv_show_name_as: "Show my name as:",
  rv_name_full: "Full name",
  rv_name_first: "First name only",
  rv_name_initials: "Initials only",
  rv_will_display: "will display as",
  rv_email_ph: "Your email (optional — earns a 'Verified customer' badge; never shown)",
  rv_comment_ph: "Write your review in the language you chose above…",
  rv_translate_q: "Would you like to translate your comment and repost it in English, as well?",
  rv_comment_en_ph: "Optional: paste your own English translation here…",
  rv_submit: "Submit review",
  rv_moderated: "Reviews are moderated and appear once approved.",
  rv_show_in: "Show reviews in:",
  rv_all_langs: "All languages",
  rv_loading: "Loading…",
  rv_none_lang: "No reviews in this language.",
  rv_verified: "Verified customer",
  rv_anon: "Anonymous",
  rv_err_rating: "Please choose a star rating.",
  rv_err_comment: "Please write a short review.",
  rv_thanks: "Thank you — your review will appear once approved.",
  nav_reviews: "Reviews",
  rv_page_subtitle: "Moderated, 7-star reviews across the FopFo™ suite, plus the quick feature summary.",
  rv_back_to_solutions: "Back to Solutions",
  rv_on_to_reviews: "On to Reviews",
  rv_see_all: "See all reviews",
  rv_all_programs: "All programs",
  rv_any_rating: "Any rating",
  rv_stars_word: "stars",
  rv_show_word: "Show",
  rv_page_word: "page",
  rv_sort_newest: "Newest first",
  rv_sort_highest: "Highest rated",
  rv_sort_lowest: "Lowest rated",
  rv_layout_reviews_first: "Show reviews first",
  rv_layout_features_first: "Show feature summary first",
  rv_none_match: "No reviews match these filters yet.",
  rv_en_label: "EN",
  // per-language SEO descriptions for legal pages
  seo_privacy_desc: "How the Full Option Family Office suite handles your data and privacy.",
  seo_terms_desc: "The terms governing use of the Full Option Family Office suite.",
  seo_cookies_desc: "How the Full Option Family Office suite uses cookies, local storage and tracking technologies.",
  seo_backup_desc: "How your data is stored, backed up and recovered across the Full Option Family Office suite.",
  seo_eula_desc: "The End User Licence Agreement for the native Full Option Family Office applications.",
  seo_copyright_desc: "The copyright and intellectual-property policy for the Full Option Family Office suite.",
};

// Maps the canonical English subject value (kept as the submitted value) to its translatable key.
const SUBJECT_KEYS = {
  "Product or Service Vendor query": "subj_vendor", "Employment Query": "subj_employment",
  "Media and Publicity": "subj_media", "Customer Support Callback": "subj_support",
  "Solution Sales query": "subj_sales", "Topic Not Found": "subj_topic", "Request Payment Link": "subj_payment",
  "Privacy query": "subj_privacy",
  "SAAS Manager": "subj_saas_manager", "SAAS Notices": "subj_saas_notices",
  "SAAS Dispute": "subj_saas_dispute", "SAAS Executive": "subj_saas_executive",
  "Children under 13": "subj_children13", "Misuse or Misconduct": "subj_misuse",
};

// Fetches (and caches server-side) localized marketing strings for the active language.
// English renders instantly; non-English swaps in once the on-demand translation resolves.
function useLanding() {
  const { lang } = useApp();
  const [strings, setStrings] = useState(LANDING_EN);
  useEffect(() => {
    let alive = true;
    if (!lang || lang === "ENG") { setStrings(LANDING_EN); return; }
    client.get(`/site/landing?lang=${lang}`)
      .then((r) => { if (alive) setStrings({ ...LANDING_EN, ...(r.data?.strings || {}) }); })
      .catch(() => { if (alive) setStrings(LANDING_EN); });
    return () => { alive = false; };
  }, [lang]);
  return (k) => strings[k] ?? LANDING_EN[k] ?? "";
}


function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
}
function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

// Emits <link rel="alternate" hreflang="..."> for every public language + x-default,
// so search engines index each language version separately. All 15 public languages
// (incl. Hindi/Urdu/Tagalog) are translated + warmed and therefore advertised.
function setAlternates(pathname, origin) {
  document.head.querySelectorAll('link[rel="alternate"][data-seo-alt]').forEach((el) => el.remove());
  const base = origin + pathname;
  const add = (hreflang, href) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    el.setAttribute("data-seo-alt", "1");
    document.head.appendChild(el);
  };
  PUBLIC_LANGS.forEach((l) => {
    const code = HREFLANG[l.code];
    if (!code) return;
    add(code, l.code === "ENG" ? base : `${base}?lang=${l.code}`);
  });
  add("x-default", base);
}

function useSeo(title, desc, opts = {}) {
  const { lang } = useApp();
  const optsKey = JSON.stringify(opts);
  useEffect(() => {
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const url = lang && lang !== "ENG" ? `${origin}${pathname}?lang=${lang}` : `${origin}${pathname}`;
    const image = opts.image || `${origin}/brand-logo.png`;
    document.title = title;
    setMeta("name", "description", desc);
    setCanonical(url);
    setAlternates(pathname, origin);
    setMeta("property", "og:locale", HREFLANG[lang] || "en");
    setMeta("property", "og:site_name", "Full Option Family Office");
    setMeta("property", "og:type", opts.type || "website");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc || "");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc || "");
    setMeta("name", "twitter:image", image);
    let ld = document.getElementById("seo-jsonld");
    if (opts.jsonLd) {
      if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "seo-jsonld"; document.head.appendChild(ld); }
      ld.textContent = JSON.stringify(opts.jsonLd);
    } else if (ld) { ld.remove(); }
    return () => { const s = document.getElementById("seo-jsonld"); if (s) s.remove(); };
  }, [title, desc, optsKey, lang]); // eslint-disable-line react-hooks/exhaustive-deps
}

const ORG_LD = () => ({
  "@context": "https://schema.org", "@type": "Organization",
  name: "Full Option Family Office", legalName: "GCA", url: window.location.origin,
  logo: `${window.location.origin}/brand-logo.png`,
  foundingDate: "2025", foundingLocation: "Edmonton, Alberta, Canada",
  contactPoint: { "@type": "ContactPoint", email: "gcacontact@simprime-ca.com", contactType: "sales" },
});

export function LangDropdown({ testid = "lang-dropdown" }) {
  const { lang, setLang } = useApp();
  return (
    <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid={testid}
      className="rounded-md border border-black/15 dark:border-white/15 bg-white/70 dark:bg-black/40 px-2 py-1.5 text-sm">
      {PUBLIC_LANGS.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
    </select>
  );
}

// Site map dropdown: quick jump to every public page, shown between language and Sign in.
export function SiteMapDropdown({ testid = "marketing-sitemap" }) {
  const t = useLanding();
  const nav = useNavigate();
  const LINKS = [
    ["/", t("nav_home") || "Home"],
    ["/solutions", t("nav_solutions")],
    ["/reviews", t("nav_reviews") || "Reviews"],
    ["/blog", t("nav_blog")],
    ["/about", t("nav_about")],
    ["/newsletter", "FIDUX\u2122"],
    ["/contact", t("nav_contact")],
    ["/login", t("sign_in") || "Sign in"],
    ["/privacy", t("footer_privacy")],
    ["/cookies", t("footer_cookies") || "Cookies Policy"],
    ["/backup", t("footer_backup") || "Backup & Recovery Policy"],
    ["/eula", t("footer_eula") || "EULA"],
    ["/terms", t("footer_terms")],
  ];
  return (
    <select value="" onChange={(e) => { if (e.target.value) nav(e.target.value); }} data-testid={testid}
      aria-label={t("nav_sitemap") || "Site map"}
      className="rounded-md border border-black/15 dark:border-white/15 bg-white/70 dark:bg-black/40 px-2 py-1.5 text-sm">
      <option value="" disabled>{t("nav_sitemap") || "Site map"}</option>
      {LINKS.map(([to, label]) => <option key={to} value={to}>{label}</option>)}
    </select>
  );
}

const TABS = [
  ["/solutions", "Solutions", Lightbulb],
  ["/reviews", "Reviews", Star],
  ["/blog", "Blog", BookOpen],
  ["/about", "About", Info],
  ["/newsletter", "FIDUX™", Newspaper],
  ["/contact", "Contact", Mail],
];

export function MarketingNav() {
  const b = useBranding();
  const t = useLanding();
  const nav = useNavigate();
  const NAV_KEYS = { Solutions: "nav_solutions", Reviews: "nav_reviews", Blog: "nav_blog", About: "nav_about", Contact: "nav_contact" };
  return (
    <header className="sticky top-0 z-40 flex flex-wrap items-center gap-3 border-b border-black/10 dark:border-white/10 glass px-5 py-3" data-testid="marketing-nav">
      <Link to="/" className="flex items-center gap-2">
        <img src="/brand-logo.png" alt="logo" className="h-8 w-8 object-contain" />
        <span className="font-head text-base font-bold" style={{ color: "var(--logo)" }}>{b?.suite_name || "Full Option Family Office"}</span>
      </Link>
      <nav className="ms-auto flex flex-wrap items-center gap-1">
        {TABS.map(([to, label, Icon]) => (
          <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/10">
            <Icon size={15} /> {NAV_KEYS[label] ? t(NAV_KEYS[label]) : label}
          </Link>
        ))}
        <LangDropdown testid="marketing-lang" />
        <SiteMapDropdown testid="marketing-sitemap" />
        <Button onClick={() => nav("/login")} data-testid="nav-signin" className="ms-1">{t("sign_in")}</Button>
      </nav>
    </header>
  );
}

function Shell({ children }) {
  const b = useBranding();
  const t = useLanding();
  return (
    <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
      <MarketingNav />
      <main className="mx-auto max-w-5xl px-5 py-10">{children}</main>
      <footer className="border-t border-black/10 dark:border-white/10 bg-white/70 dark:bg-neutral-900/70 px-5 py-6 text-center text-xs opacity-90 backdrop-blur-sm">
        <div className="mx-auto max-w-3xl space-y-1">
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 font-medium opacity-80" data-testid="footer-legal-links">
            <Link to="/about" className="hover:text-[var(--primary)]">{t("nav_about")}</Link>
            <span className="opacity-40">·</span>
            <Link to="/solutions" className="hover:text-[var(--primary)]" data-testid="footer-solutions-link">{t("footer_solutions") || "Solutions"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/reviews" className="hover:text-[var(--primary)]" data-testid="footer-reviews-link">{t("nav_reviews") || "Reviews"}</Link>
            <span className="opacity-40">·</span>
            <a href={`${API}/public/specifications.pdf`} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]" data-testid="footer-specifications-link">{t("footer_specs") || "Specifications"}</a>
            <span className="opacity-40">·</span>
            <Link to="/contact" className="hover:text-[var(--primary)]">{t("nav_contact")}</Link>
            <span className="opacity-40">·</span>
            <Link to="/backup" className="hover:text-[var(--primary)]" data-testid="footer-backup-link">{t("footer_backup") || "Backup & Recovery Policy"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/cookies" className="hover:text-[var(--primary)]" data-testid="footer-cookies-link">{t("footer_cookies") || "Cookies Policy"}</Link>
          </div>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 font-medium opacity-80" data-testid="footer-legal-links-2">
            <Link to="/privacy" className="hover:text-[var(--primary)]" data-testid="footer-privacy-link">{t("footer_privacy") || "Privacy Policy"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/eula" className="hover:text-[var(--primary)]" data-testid="footer-eula-link">{t("footer_eula") || "EULA"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/copyright" className="hover:text-[var(--primary)]" data-testid="footer-copyright-link">{t("footer_copyright") || "Copyright Policy"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/terms" className="hover:text-[var(--primary)]" data-testid="footer-terms-link">{t("footer_terms") || "Terms of Use"}</Link>
          </div>
          <div className="opacity-70" data-testid="footer-copyright">{(b?.copyright_notice || "Copyright GCA 2026  All Rights Reserved").replace(/All Rights Reserved/i, t("rights_reserved"))}</div>
          {(() => {
            const lang = (typeof localStorage !== "undefined" && localStorage.getItem("suite_lang")) || "ENG";
            const tm = b?.trademark_notice && (b.trademark_notice[lang] || b.trademark_notice.ENG);
            return tm ? <div data-testid="marketing-trademark-notice" className="text-[10px] leading-tight opacity-60">{tm}</div> : null;
          })()}
        </div>
      </footer>
    </div>
  );
}

function ShareBar({ title }) {
  const [copied, setCopied] = useState(false);
  const url = window.location.href;
  const enc = encodeURIComponent;
  const x = `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
  };
  const cls = "flex h-9 w-9 items-center justify-center rounded-full border border-black/15 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/10 lift";
  return (
    <div className="mt-10 flex items-center gap-2 border-t border-black/10 dark:border-white/10 pt-5" data-testid="share-bar">
      <span className="me-1 text-sm opacity-60">Share:</span>
      <a href={x} target="_blank" rel="noopener noreferrer" className={cls} data-testid="share-x" aria-label="Share on X"><Twitter size={16} /></a>
      <a href={li} target="_blank" rel="noopener noreferrer" className={cls} data-testid="share-linkedin" aria-label="Share on LinkedIn"><Linkedin size={16} /></a>
      <button onClick={copy} data-testid="share-copy" aria-label="Copy link"
        className="flex h-9 items-center gap-1.5 rounded-full border border-black/15 dark:border-white/15 px-3 text-sm hover:bg-black/5 dark:hover:bg-white/10 lift">
        {copied ? <Check size={15} /> : <Link2 size={15} />} {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}

function SubscribeCTA() {
  const L = useLanding();
  const [form, setForm] = useState({ name: "", email: "" });
  const [msg, setMsg] = useState("");
  const sub = async () => {
    if (!form.name || !form.email) { setMsg("Enter your name and email."); return; }
    try { const { data } = await client.post("/site/newsletter/subscribe", { ...form, lang: (localStorage.getItem("suite_lang") || "ENG") }); setMsg(`Status: ${data.status.replace("_", " ")}.`); }
    catch (e) { setMsg(apiErr(e)); }
  };
  return (
    <Card className="mt-10" data-testid="subscribe-cta">
      <div className="flex items-start gap-3">
        <Newspaper size={20} className="mt-1 text-[var(--primary)]" />
        <div className="flex-1">
          <h3 className="font-head text-xl mb-1">Subscribe to FIDUX™</h3>
          <p className="text-sm opacity-70 mb-4" data-testid="cta-subtitle">{L("fidux_subtitle")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <Input label="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cta-name" className="max-w-[200px]" />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cta-email" className="max-w-[240px]" />
            <Button onClick={sub} data-testid="cta-subscribe">Subscribe</Button>
          </div>
          {msg && <p className="mt-2 text-sm opacity-70" data-testid="cta-msg">{msg}</p>}
        </div>
      </div>
    </Card>
  );
}

function linkify(text, keyBase) {
  const out = [];
  const re = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    let raw = m[0];
    let trail = "";
    const tm = raw.match(/[.,;:!?]+$/);
    if (tm) { trail = tm[0]; raw = raw.slice(0, -trail.length); }
    const isEmail = raw.includes("@") && !/^https?:\/\//.test(raw);
    const href = isEmail ? `mailto:${raw}` : (raw.startsWith("www.") ? `https://${raw}` : raw);
    out.push(<a key={`${keyBase}-l${i++}`} href={href} {...(isEmail ? {} : { target: "_blank", rel: "noopener noreferrer" })}
      className="text-[var(--primary)] underline break-words hover:opacity-80">{raw}</a>);
    if (trail) out.push(trail);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function subAndLink(text, keyBase) {
  const out = []; const re = /\^\{([^}]+)\}/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...linkify(text.slice(last, m.index), `${keyBase}p${i}`));
    out.push(<sup key={`${keyBase}s${i}`}>{linkify(m[1], `${keyBase}si${i}`)}</sup>);
    i++; last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...linkify(text.slice(last), `${keyBase}p${i}`));
  return out;
}

function fmtInline(text) {
  const out = []; const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\^\{[^}]+\})/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...subAndLink(text.slice(last, m.index), `p${i}`));
    const tok = m[0];
    if (tok.startsWith("**")) out.push(<strong key={i++}>{subAndLink(tok.slice(2, -2), `b${i}`)}</strong>);
    else if (tok.startsWith("^{")) out.push(<sup key={i++}>{linkify(tok.slice(2, -1), `s${i}`)}</sup>);
    else out.push(<em key={i++}>{subAndLink(tok.slice(1, -1), `e${i}`)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(...subAndLink(text.slice(last), `p${i}`));
  return out;
}

function LegalBlock({ block }) {
  const trimmed = (block || "").trim();
  if (!trimmed) return null;
  const isCallout = /^\*\*[\s\S]+\*\*$/.test(trimmed) && trimmed.slice(2, -2).indexOf("**") === -1;
  if (isCallout) {
    return <p className="mt-6 font-bold text-base sm:text-lg leading-relaxed" data-testid="legal-callout">{fmtInline(trimmed.slice(2, -2))}</p>;
  }
  const lines = block.split("\n");
  const hm = lines[0].match(/^(\d+\.\d+(?:\.\d+)?)\s+(.*)$/);
  if (hm) {
    const body = lines.slice(1).join("\n").trim();
    return (
      <div>
        <h3 className="mt-6 mb-1 font-head text-base sm:text-lg font-semibold" data-testid="legal-heading">{hm[1]} {fmtInline(hm[2])}</h3>
        {body && <p className="whitespace-pre-line text-sm leading-relaxed opacity-90">{fmtInline(body)}</p>}
      </div>
    );
  }
  return <p className="whitespace-pre-line text-sm leading-relaxed opacity-90">{fmtInline(block)}</p>;
}

function LegalPage({ doc, testid }) {
  const { lang } = useApp();
  const L = useLanding();
  const [data, setData] = useState(null);
  const fallbackTitle = doc === "terms" ? "Terms of Use" : doc === "cookies" ? "Cookies Policy" : doc === "backup" ? "Backup & Recovery Policy" : doc === "eula" ? "End User Licence Agreement (EULA)" : doc === "copyright" ? "Copyright Policy" : "Privacy Policy";
  useSeo(`${data?.title || fallbackTitle} — Full Option Family Office`, L(`seo_${doc}_desc`));
  useEffect(() => {
    let live = true;
    client.get("/public/legal", { params: { doc, lang } })
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData(false); });
    return () => { live = false; };
  }, [doc, lang]);
  return (
    <Shell>
      <article className="mx-auto max-w-3xl" data-testid={testid}>
        <h1 className="font-head text-3xl sm:text-4xl mb-2">{data?.title || fallbackTitle}</h1>
        {data && data !== false && (
          <p className="text-xs opacity-60 mb-8" data-testid={`${testid}-meta`}>
            {data.suite_name} · Version {data.version} · Last updated {(data.last_modified || "").slice(0, 10)}
          </p>
        )}
        {data === false && <p className="text-sm opacity-70">Unable to load this document right now. Please try again shortly.</p>}
        {data === null && <p className="text-sm opacity-60">Loading…</p>}
        {data && data !== false && (
          <div className="space-y-3" data-testid={`${testid}-content`}>
            {(data.content || "").split("\n\n").map((p, i) => (
              <LegalBlock key={i} block={p} />
            ))}
          </div>
        )}
      </article>
    </Shell>
  );
}

export function TermsPage() { return <LegalPage doc="terms" testid="terms-page" />; }
export function PrivacyPage() { return <LegalPage doc="privacy" testid="privacy-page" />; }
export function CookiesPage() { return <LegalPage doc="cookies" testid="cookies-page" />; }
export function BackupPage() { return <LegalPage doc="backup" testid="backup-page" />; }
export function EulaPage() { return <LegalPage doc="eula" testid="eula-page" />; }
export function CopyrightPage() { return <LegalPage doc="copyright" testid="copyright-page" />; }

export function Blog() {
  const [posts, setPosts] = useState([]);
  const L = useLanding();
  const b = useBranding();
  const { lang } = useApp();
  const origin = window.location.origin;
  const longBlogUrl = b?.long_blog_url || "";
  useSeo("Blog — Full Option Family Office", L("seo_blog_desc"), {
    jsonLd: {
      "@context": "https://schema.org", "@type": "Blog", name: "Full Option Family Office Blog",
      url: `${origin}/blog`, publisher: ORG_LD(),
      blogPost: posts.map((p) => ({ "@type": "BlogPosting", headline: p.title,
        description: p.excerpt, articleSection: p.category, url: `${origin}/blog/${p.slug}` })),
    },
  });
  useEffect(() => { client.get("/site/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);
  return (
    <Shell>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div>
          <h1 className="font-head text-4xl mb-2" data-testid="short-blog-title">{L("blog_title_short")}</h1>
          <p className="opacity-60" data-testid="blog-subtitle">{L("blog_subtitle")}</p>
        </div>
        <div className="sm:text-end">
          <h2 className="font-head text-4xl mb-2" data-testid="long-blog-title">{L("blog_title_long")}</h2>
          {longBlogUrl
            ? <a href={longBlogUrl} target="_blank" rel="noopener noreferrer" data-testid="long-blog-url"
                className="text-sm text-[var(--primary)] underline decoration-dotted break-all hover:opacity-80">{longBlogUrl}</a>
            : <span className="text-sm opacity-40" data-testid="long-blog-url-empty">No external URL set</span>}
        </div>
      </div>
      {lang && lang !== "ENG" && <p className="mb-6 flex items-center gap-1.5 text-xs opacity-60" data-testid="blog-autotranslate"><Languages size={13} className="text-[var(--primary)]" /> {L("auto_translated_notice")}</p>}
      <div className="grid gap-5 sm:grid-cols-2" data-testid="blog-list">
        {posts.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}>
            <Card className="h-full lift">
              <div className="text-xs uppercase tracking-wide opacity-50">{p.category}</div>
              <h2 className="font-head text-lg mt-1">{p.title}</h2>
              <p className="text-sm opacity-70 mt-2">{p.excerpt}</p>
            </Card>
          </Link>
        ))}
      </div>
    </Shell>
  );
}

function RelatedArticles({ slug, category }) {
  const [rel, setRel] = useState([]);
  useEffect(() => {
    client.get("/site/blog").then((r) => {
      const all = r.data.filter((p) => p.slug !== slug);
      const same = all.filter((p) => p.category === category);
      const rest = all.filter((p) => p.category !== category);
      setRel([...same, ...rest].slice(0, 3));
    }).catch(() => {});
  }, [slug, category]);
  if (!rel.length) return null;
  return (
    <div className="mt-14 border-t border-black/10 dark:border-white/10 pt-8" data-testid="related-articles">
      <h2 className="font-head text-2xl mb-5">Related articles</h2>
      <div className="grid gap-5 sm:grid-cols-3">
        {rel.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-card-${p.slug}`}>
            <Card className="h-full lift">
              <div className="text-xs uppercase tracking-wide opacity-50">{p.category}</div>
              <h3 className="font-head text-base mt-1">{p.title}</h3>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

const SITE_LANGS = PUBLIC_LANGS.map((l) => [l.code, l.native, !!l.placeholder]);

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [lang, setLang] = useState((typeof window !== "undefined" && localStorage.getItem("suite_lang")) || "ENG");
  const [translating, setTranslating] = useState(false);
  useEffect(() => {
    setTranslating(true);
    client.get(`/site/blog/${slug}`, { params: { lang } }).then((r) => setP(r.data)).catch(() => setP(false)).finally(() => setTranslating(false));
  }, [slug, lang]);
  const origin = window.location.origin;
  useSeo(p ? `${p.title} — Full Option Family Office` : "Article", p ? p.excerpt : "", p ? {
    type: "article", image: `${API}/site/og/${p.slug}.png`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Article",
      headline: p.title, description: p.excerpt, articleSection: p.category,
      keywords: (p.tags || []).join(", "),
      author: { "@type": "Organization", name: "GCA" },
      publisher: {
        "@type": "Organization", name: "Full Option Family Office",
        logo: { "@type": "ImageObject", url: `${origin}/brand-logo.png` },
      },
      datePublished: p.created_at, dateModified: p.created_at,
      mainEntityOfPage: `${origin}/blog/${p.slug}`,
    },
  } : {});
  if (p === false) return <Shell><p>Article not found. <Link to="/blog" className="text-[var(--primary)]">Back to blog</Link></p></Shell>;
  if (!p) return <Shell><p>Loading…</p></Shell>;
  return (
    <Shell>
      <article data-testid="blog-article" className="mx-auto max-w-2xl">
        <div className="flex items-center gap-3">
          <Link to="/blog" className="text-sm text-[var(--primary)]">← All articles</Link>
          <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="blog-lang-select"
            className="ms-auto rounded-full border border-black/15 dark:border-white/15 bg-transparent px-3 py-1 text-xs">
            {SITE_LANGS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
        {translating && lang !== "ENG" && <div className="mt-2 text-xs opacity-60" data-testid="blog-translating">Translating…</div>}
        <div className="text-xs uppercase tracking-wide opacity-50 mt-4">{p.category}</div>
        <h1 className="font-head text-3xl sm:text-4xl mt-1 mb-6">{p.title}</h1>
        {p.body.split("\n\n").map((para, i) => <p key={i} className="mb-4 leading-relaxed opacity-90">{para}</p>)}
        <SubscribeCTA />
        <ShareBar title={p.title} />
      </article>
      <RelatedArticles slug={p.slug} category={p.category} />
    </Shell>
  );
}

const TM_LINKS = {
  "Familigi™": "family-tree-application",
  "SEBAA™": "benefits-management-application",
  "Currencida™": "currency-management-application",
  "BoxAlong™": "holdings-management-application",
  "FopFo™": "suite",
};

// Trademark rendered in brand blue and hyperlinked to its Solutions product card.
function TM({ name }) {
  const slug = TM_LINKS[name];
  return (
    <Link to={`/solutions#sol-${slug}`} data-testid={`tm-link-${slug}`}
      className="font-medium text-[var(--primary)] hover:underline">{name}</Link>
  );
}

// Renders localized rich copy: **bold**, _italic_, and product trademark names (Familigi™ …) as product links.
function RichText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*.+?\*\*|_.+?_|[A-Za-z]+™)/g);
  return parts.map((seg, i) => {
    if (!seg) return null;
    if (seg.startsWith("**") && seg.endsWith("**") && seg.length > 4) return <b key={i}>{seg.slice(2, -2)}</b>;
    if (seg.startsWith("_") && seg.endsWith("_") && seg.length > 2) return <i key={i}>{seg.slice(1, -1)}</i>;
    if (/^[A-Za-z]+™$/.test(seg) && TM_LINKS[seg]) return <TM key={i} name={seg} />;
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
}

export function About() {
  const t = useLanding();
  useSeo("About GCA — Full Option Family Office", t("seo_about_desc"));
  return (
    <Shell>
      <h1 className="font-head text-4xl mb-6" data-testid="about-title">{t("about_title")}</h1>
      <div className="prose max-w-2xl space-y-4 leading-relaxed opacity-90" data-testid="about-content">
        {["about_p1", "about_p2", "about_p3", "about_p4", "about_p5", "about_p6"].map((k) => (
          <p key={k}><RichText text={t(k)} /></p>
        ))}
      </div>
    </Shell>
  );
}

export function Newsletter() {
  const L = useLanding();
  const { lang } = useApp();
  const [tab, setTab] = useState("subscribe");
  const [form, setForm] = useState({ name: "", email: "" });
  const [msg, setMsg] = useState("");
  const [teasers, setTeasers] = useState([]);
  const [params] = useSearchParams();
  useSeo("FIDUX — Full Option Family Office", L("seo_fidux_desc"));
  useEffect(() => { client.get("/site/newsletter/issues").then((r) => setTeasers(r.data)).catch(() => {}); }, []);
  // One-click unsubscribe from an email link: /newsletter?unsub=<email>
  useEffect(() => {
    const em = params.get("unsub");
    if (em) {
      setTab("unsubscribe"); setForm((f) => ({ ...f, email: em }));
      client.post("/site/newsletter/unsubscribe", { name: "", email: em })
        .then(() => setMsg(`${em} has been unsubscribed from FIDUX.`))
        .catch((e) => setMsg(apiErr(e)));
    }
    // eslint-disable-next-line
  }, []);
  const [welcome, setWelcome] = useState(true);
  const act = async (path) => {
    if (!form.email || (path === "subscribe" && !form.name)) { setMsg("Enter your name and email."); return; }
    try {
      const ref = params.get("ref");
      const payload = { name: form.name, email: form.email, lang: lang || "ENG" };
      if (path === "subscribe") { payload.welcome = welcome; if (ref) payload.source = ref; }
      const { data } = await client.post(`/site/newsletter/${path}`, payload);
      setMsg(data.welcome_sent ? "Subscribed — the latest edition is on its way to your inbox." : `Status: ${data.status.replace("_", " ")}.`);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const [beEmail, setBeEmail] = useState("");
  const [beMsg, setBeMsg] = useState("");
  const [beBusy, setBeBusy] = useState("");
  const emailMe = async (slug) => {
    const em = (beEmail || form.email || "").trim();
    if (!em) { setBeMsg(L("nl_backissue_email")); return; }
    setBeBusy(slug); setBeMsg("");
    try {
      await client.post(`/site/newsletter/issues/${slug}/email-me`, { email: em });
      setBeMsg(`✓ Sent to ${em}. Please check your inbox.`);
    } catch (e) { setBeMsg(apiErr(e)); }
    finally { setBeBusy(""); }
  };
  const getArchive = async () => {
    const em = (beEmail || form.email || "").trim();
    if (!em) { setBeMsg(L("nl_backissue_email")); return; }
    setBeBusy("__archive__"); setBeMsg("");
    try {
      const { data } = await client.post("/site/newsletter/archive-to-me", { email: em });
      setBeMsg(`✓ Sent ${data.count} edition${data.count === 1 ? "" : "s"} to ${em}. Please check your inbox.`);
    } catch (e) { setBeMsg(apiErr(e)); }
    finally { setBeBusy(""); }
  };
  return (
    <Shell>
      <h1 className="font-head text-4xl mb-4" data-testid="fidux-heading">FIDUX™ — {L("fidux_subtitle")}</h1>
      {lang && lang !== "ENG" && <p className="mb-4 flex items-center gap-1.5 text-xs opacity-60" data-testid="nl-autotranslate"><Languages size={13} className="text-[var(--primary)]" /> {L("auto_translated_notice")}</p>}
      <Card className="max-w-2xl mb-6 border-s-4 border-[var(--primary)]" data-testid="newsletter-access-note">
        <p className="text-sm opacity-80">{L("nl_access1")}</p>
        <p className="text-sm opacity-70 mt-2">{L("nl_access2")}</p>
      </Card>
      <Card className="max-w-lg mb-8" data-testid="newsletter-form">
        <div className="mb-3 flex gap-2">
          {["subscribe", "unsubscribe"].map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} data-testid={`newsletter-tab-${tb}`}
              className={`rounded-full px-3 py-1 text-sm ${tab === tb ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>
              {tb === "subscribe" ? L("nl_subscribe") : L("nl_unsubscribe")}</button>
          ))}
        </div>
        <div className="space-y-3">
          {tab === "subscribe" && <Input label={L("nl_full_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="newsletter-name" />}
          <Input label={L("nl_email")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="newsletter-email" />
          {tab === "subscribe" && (
            <label className="flex items-center gap-2 text-sm opacity-80" data-testid="newsletter-welcome-label">
              <input type="checkbox" checked={welcome} onChange={(e) => setWelcome(e.target.checked)} data-testid="newsletter-welcome" />
              Send me the latest edition now
            </label>
          )}
          <Button onClick={() => act(tab)} data-testid="newsletter-submit">{tab === "subscribe" ? L("nl_subscribe") : L("nl_unsubscribe")}</Button>
          {msg && <p className="text-sm opacity-70" data-testid="newsletter-msg">{msg}</p>}
        </div>
      </Card>
      <p className="mb-8 text-sm"><Link to="/newsletter/share" className="text-[var(--primary)]" data-testid="newsletter-share-link">{L("nl_share")}</Link></p>
      <h2 className="font-head text-2xl mb-4">{L("nl_recent")}</h2>
      <div className="max-w-lg mb-4" data-testid="backissue-panel">
        <p className="mb-2 text-sm opacity-70">{L("nl_backissue_hint")}</p>
        <Input label={L("nl_backissue_email")} type="email" value={beEmail}
          onChange={(e) => setBeEmail(e.target.value)} data-testid="backissue-email" />
        <button onClick={getArchive} disabled={beBusy === "__archive__"} data-testid="backissue-archive"
          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--primary)] px-4 py-1.5 text-sm text-white transition hover:opacity-90 disabled:opacity-50">
          <Download size={14} /> {beBusy === "__archive__" ? L("nl_sending") : L("nl_get_archive")}
        </button>
        {beMsg && <p className="mt-2 text-sm opacity-80" data-testid="backissue-msg">{beMsg}</p>}
      </div>
      <div className="space-y-3" data-testid="newsletter-issues">
        {teasers.map((n) => (
          <Card key={n.slug}>
            <div className="flex flex-wrap items-center gap-2" data-testid={`newsletter-locked-${n.slug}`}>
              <div>
                <div className="text-xs opacity-50">{n.date_label}</div>
                <h3 className="font-head text-lg">{n.title}</h3>
              </div>
              <div className="ms-auto flex items-center gap-3">
                <span className="flex items-center gap-1 text-xs opacity-60"><Mail size={13} /> {L("nl_delivered")}</span>
                <button onClick={() => emailMe(n.slug)} disabled={beBusy === n.slug} data-testid={`backissue-send-${n.slug}`}
                  className="rounded-full border border-[var(--primary)] px-3 py-1 text-xs text-[var(--primary)] transition hover:bg-[var(--primary)] hover:text-white disabled:opacity-50">
                  {beBusy === n.slug ? L("nl_sending") : L("nl_email_me")}
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Shell>
  );
}

export function NewsletterIssue() {
  const L = useLanding();
  const { slug } = useParams();
  const [n, setN] = useState(null);
  useEffect(() => { client.get(`/site/newsletter/issues/${slug}`).then((r) => setN(r.data)).catch(() => setN(false)); }, [slug]);
  useSeo(n ? `${n.title} — Full Option Family Office` : "FIDUX", "Subscriber-only newsletter issue", {});
  if (n === false) return <Shell><p>Issue not found. <Link to="/newsletter" className="text-[var(--primary)]">Back to FIDUX</Link></p></Shell>;
  if (!n) return <Shell><p>Loading…</p></Shell>;
  return (
    <Shell>
      <article data-testid="newsletter-article-locked" className="mx-auto max-w-2xl">
        <Link to="/newsletter" className="text-sm text-[var(--primary)]">← All issues</Link>
        <div className="text-xs uppercase tracking-wide opacity-50 mt-4">FIDUX™ — {L("fidux_subtitle")} · {n.date_label}</div>
        <h1 className="font-head text-3xl sm:text-4xl mt-1 mb-4">{n.title}</h1>
        <Card className="border-s-4 border-[var(--primary)]">
          <div className="flex items-center gap-2 mb-2"><Lock size={18} className="text-[var(--primary)]" /><span className="font-semibold">Subscriber-only content</span></div>
          <p className="text-sm opacity-80 mb-3">FIDUX is delivered by email to subscribers. Access is by subscription only, at no cost, as a courtesy for our active customers in good standing.</p>
          <Link to="/newsletter" className="inline-block rounded-md bg-[var(--primary)] px-4 py-2 text-sm text-white lift" data-testid="newsletter-subscribe-link">Subscribe (free) to receive issues →</Link>
        </Card>
      </article>
    </Shell>
  );
}

export function NewsletterShare() {
  const [teasers, setTeasers] = useState([]);
  const [form, setForm] = useState({ slug: "", from_email: "", to_email: "", note: "" });
  const [msg, setMsg] = useState("");
  const [params] = useSearchParams();
  useSeo("Share a FIDUX issue — Full Option Family Office", "Share FIDUX™ with a colleague.");
  useEffect(() => {
    client.get("/site/newsletter/issues").then((r) => {
      setTeasers(r.data);
      const pre = params.get("issue");
      setForm((f) => ({ ...f, slug: pre || (r.data[0]?.slug || "") }));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const share = async () => {
    if (!form.slug || !form.from_email || !form.to_email) { setMsg("Choose an issue and enter both emails."); return; }
    try { const { data } = await client.post("/site/newsletter/share", form); setMsg(`Shared with ${data.to}. Thank you for spreading the word!`); }
    catch (e) { setMsg(apiErr(e)); }
  };
  return (
    <Shell>
      <h1 className="font-head text-4xl mb-3">Share a FIDUX™ issue</h1>
      <Card className="max-w-lg" data-testid="newsletter-share-form">
        <p className="text-sm opacity-70 mb-4">Send a colleague an issue you enjoyed. They'll receive it with a free subscribe option — and nothing to unsubscribe from, since they aren't on our list.</p>
        <div className="space-y-3">
          <label className="block text-sm">Issue
            <select value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="share-issue"
              className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-2 text-sm">
              {teasers.map((n) => <option key={n.slug} value={n.slug}>{n.title}</option>)}
            </select>
          </label>
          <Input label="Your subscriber email" type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} data-testid="share-from" />
          <Input label="Colleague's email" type="email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} data-testid="share-to" />
          <Input label="Personal note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="share-note" />
          <Button onClick={share} data-testid="share-submit">Share issue</Button>
          {msg && <p className="text-sm opacity-70" data-testid="share-msg">{msg}</p>}
        </div>
      </Card>
      <p className="mt-4 text-sm"><Link to="/newsletter" className="text-[var(--primary)]">← Back to FIDUX</Link></p>
    </Shell>
  );
}

const SUBJECTS = ["Topic Not Found", "Solution Sales query", "Request Payment Link",
  "Product or Service Vendor query", "Privacy query", "Media and Publicity",
  "Employment Query", "Customer Support Callback",
  "SAAS Manager", "SAAS Notices", "SAAS Dispute", "SAAS Executive",
  "Children under 13", "Misuse or Misconduct"];

const SALES_LABELS = {
  family_tree: "Familigi™ — Family Tree Application",
  holdings: "BoxAlong™ — Holdings Management Application",
  currency: "Currencida™ — Currency Management Application",
  benefits: "SEBAA™ — Benefits Management Application",
  suite: "FopFo™ Full Suite (all 4 applications)",
};

export function Contact() {
  const t = useLanding();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", comment: "", subject: "Product or Service Vendor query", want_receipt: false });
  const [msg, setMsg] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [opts, setOpts] = useState([]);
  const [activation, setActivation] = useState("");
  const [payMsg, setPayMsg] = useState("");
  const [sf, setSf] = useState(null);
  const [salesProduct, setSalesProduct] = useState("suite");
  const [salesService, setSalesService] = useState(false);
  const [showServiceWarn, setShowServiceWarn] = useState(false);
  const [params] = useSearchParams();
  const product = params.get("app") || "";
  useSeo("Contact — Full Option Family Office", t("seo_contact_desc"));

  useEffect(() => { client.get("/site/payment-options").then((r) => setOpts(r.data.options)).catch(() => {}); }, []);
  useEffect(() => { client.get("/storefront/pricing").then((r) => setSf(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("pay") === "1" && sid) {
      setShowPay(true); setPayMsg("Checking payment status…");
      const poll = (n = 0) => client.get(`/site/pay/status/${sid}`).then((r) => {
        if (r.data.payment_status === "paid") setPayMsg("✓ Payment successful. Thank you!");
        else if (r.data.status === "expired") setPayMsg("Payment session expired.");
        else if (n < 5) setTimeout(() => poll(n + 1), 2000);
        else setPayMsg("Payment still processing — check your email for confirmation.");
      }).catch(() => setPayMsg("Could not verify payment."));
      poll();
    } else if (params.get("pay") === "0") { setShowPay(true); setPayMsg("Payment cancelled."); }
    else if (params.get("pay") === "start") { setShowPay(true); }
  }, [params]);

  const submit = async () => {
    if (!f.first_name || !f.email) { setMsg("First name and email are required."); return; }
    try {
      const activeLang = localStorage.getItem("suite_lang") || "ENG";
      const subjectLabel = SUBJECT_KEYS[f.subject] ? t(SUBJECT_KEYS[f.subject]) : f.subject;
      const { data } = await client.post("/site/contact", { ...f, subject_label: subjectLabel, lang: activeLang });
      setMsg("✓ Message sent. Thank you!");
      if (data.payment_required) setShowPay(true);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const pay = async (kind, emailLink) => {
    if (kind === "sales" && !salesService) { setShowServiceWarn(true); return; }
    setPayMsg("Starting secure checkout…");
    try {
      const body = { kind, origin_url: window.location.origin, email: f.email || undefined, email_link: emailLink };
      if (product) body.product = product;
      if (kind === "activation") body.custom_amount = parseFloat(activation);
      if (kind === "sales") { body.product_key = salesProduct; body.include_service = true; body.product = SALES_LABELS[salesProduct]; }
      const { data } = await client.post("/site/pay", body);
      if (emailLink) setPayMsg(`✓ A secure payment link was emailed to ${f.email}.`);
      else window.location.href = data.url;
    } catch (e) { setPayMsg(apiErr(e)); }
  };

  const serviceAmt = (opts.find((o) => o.kind === "service") || {}).amount || 90;
  const singleP = sf ? sf.single_price : 23.70;
  const suiteP = sf ? sf.suite_price : 89.82;
  const salesPrice = salesProduct === "suite" ? suiteP : singleP;

  return (
    <Shell>
      <h1 className="font-head text-4xl mb-6" data-testid="contact-title">{t("contact_title")}</h1>
      <div className="grid gap-8 md:grid-cols-2">
        <Card data-testid="contact-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label={t("contact_first")} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="contact-first" />
            <Input label={t("contact_last")} value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="contact-last" />
          </div>
          <div className="mt-3"><Input label={t("contact_email")} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" /></div>
          <label className="mt-3 block text-sm"><span className="opacity-70">{t("contact_subject")}</span>
            <select value={f.subject} onChange={(e) => { setF({ ...f, subject: e.target.value }); if (e.target.value === "Request Payment Link") setShowPay(true); }}
              data-testid="contact-subject" className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm">
              {SUBJECTS.map((s) => ({ s, label: SUBJECT_KEYS[s] ? t(SUBJECT_KEYS[s]) : s }))
                .sort((a, b) => b.label.localeCompare(a.label))
                .map(({ s, label }) => <option key={s} value={s}>{label}</option>)}
            </select>
          </label>
          <label className="mt-3 block text-sm"><span className="opacity-70">{t("contact_comment")}</span>
            <textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="contact-comment" rows={4}
              className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
          </label>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.want_receipt} onChange={(e) => setF({ ...f, want_receipt: e.target.checked })} data-testid="contact-receipt" />
            {t("contact_receipt")}
          </label>
          <div className="mt-4"><Button onClick={submit} data-testid="contact-submit">{t("contact_send")}</Button></div>
          <input type="text" tabIndex={-1} autoComplete="off" value={f.website || ""} onChange={(e) => setF({ ...f, website: e.target.value })}
            className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="contact-hp" />
          {msg && <p className="mt-2 text-sm opacity-70" data-testid="contact-msg">{msg}</p>}
        </Card>

        {showPay && (
          <Card data-testid="payment-portal">
            <h2 className="font-head text-xl mb-1">{t("pay_secure_title")}</h2>
            {product && <div className="mb-2 inline-block rounded-full bg-[var(--primary)]/10 px-3 py-1 text-sm font-medium text-[var(--primary)]" data-testid="pay-product">{t("pay_purchasing")} {product}</div>}
            <p className="text-sm opacity-60 mb-4">{t("pay_intro")}</p>
            <div className="space-y-3">
              {opts.map((o) => (
                <div key={o.kind} className="rounded-md border border-black/10 dark:border-white/10 p-3" data-testid={`pay-opt-${o.kind}`}>
                  <div className="text-sm font-medium">{t(`pay_${o.kind}_label`)}</div>
                  {o.kind === "activation" ? (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <input type="number" min="1" placeholder={t("pay_amount_ph")} value={activation}
                          onChange={(e) => setActivation(e.target.value)} data-testid="pay-activation-amount"
                          className="w-32 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1.5 text-sm" />
                      </div>
                      <p className="text-xs opacity-60" data-testid="pay-activation-caveat">{t("activation_caveat")}</p>
                    </div>
                  ) : o.kind === "sales" ? (
                    <div className="mt-2 space-y-2" data-testid="pay-sales-block">
                      <select value={salesProduct} onChange={(e) => setSalesProduct(e.target.value)}
                        data-testid="pay-sales-product"
                        className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1.5 text-sm">
                        <option value="family_tree">Familigi™ — Family Tree</option>
                        <option value="holdings">BoxAlong™ — Holdings</option>
                        <option value="currency">Currencida™ — Currency</option>
                        <option value="benefits">SEBAA™ — Benefits</option>
                        <option value="suite">{t("pay_opt_suite")}</option>
                      </select>
                      <div className="text-sm opacity-70" data-testid="pay-sales-price">
                        {salesProduct === "suite" ? t("pay_full_suite") : t("pay_single_app")}: USD {salesPrice.toFixed(2)}
                      </div>
                      <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" checked={salesService} onChange={(e) => setSalesService(e.target.checked)}
                          data-testid="pay-sales-service" className="mt-0.5" />
                        <span>{t("pay_include_prefix")} <b>{t("pay_service_charge")}</b> (USD {serviceAmt.toFixed(2)}{t("pay_yr_account")})</span>
                      </label>
                      <div className="text-sm font-semibold" data-testid="pay-sales-total">
                        {t("pay_total")} USD {(salesPrice + (salesService ? serviceAmt : 0)).toFixed(2)}
                        <span className="ms-1 font-normal opacity-60">{t("pay_plus_tax")}</span>
                      </div>
                    </div>
                  ) : <div className="text-sm opacity-60">{o.amount?.toFixed(2)} USD</div>}
                  {o.kind === "service" && (
                    <div className="mt-2 rounded-md bg-[var(--primary)]/8 p-2 text-xs" data-testid="pay-service-coverage">
                      <div className="font-medium opacity-80">{t("pay_covers_title")}</div>
                      <ul className="mt-1 list-disc ps-4 opacity-70">
                        {[1, 2, 3, 4, 5].map((n) => <li key={n}>{t(`pay_cover_${n}`)}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => pay(o.kind, false)} data-testid={`pay-now-${o.kind}`}>{t("pay_now")}</Button>
                    <Button variant="ghost" onClick={() => pay(o.kind, true)} data-testid={`pay-email-${o.kind}`}>{t("pay_email_link")}</Button>
                  </div>
                </div>
              ))}
            </div>
            {payMsg && <p className="mt-3 text-sm opacity-80" data-testid="pay-msg">{payMsg}</p>}
          </Card>
        )}
      </div>
      {showServiceWarn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="service-warn-modal">
          <div className="w-full max-w-sm rounded-lg bg-white dark:bg-neutral-900 p-5 shadow-xl">
            <h3 className="font-head text-lg mb-1">{t("pay_warn_title")}</h3>
            <p className="text-sm opacity-75">
              {t("pay_warn_body_a")} <b>{t("pay_service_charge")}</b> (USD {serviceAmt.toFixed(2)}{t("pay_yr_account")}). {t("pay_warn_body_b")}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowServiceWarn(false)} data-testid="service-warn-close">{t("pay_go_back")}</Button>
              <Button onClick={() => { setSalesService(true); setShowServiceWarn(false); }} data-testid="service-warn-add">{t("pay_add_it")}</Button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

// Style every trademarked name (word ending in ™) bold + italic + solid black.
const renderTM = (str, kp) =>
  str.split(/([A-Za-z]+™)/).map((s, i) =>
    i % 2
      ? <strong key={`${kp}-tm-${i}`} className="font-bold italic text-black dark:text-white">{s}</strong>
      : s);


const PRODUCT_CARDS = [
  { key: "family_tree", name: "Family Tree Application", slug: "family-tree-application", img: "/solutions/family_tree.png", wordmark: "/solutions/wordmarks/family_tree.png",
    hook: "Track every relative, branch, and beneficiary across generations." },
  { key: "holdings", name: "Holdings Management Application", slug: "holdings-management-application", img: "/solutions/holdings.png", wordmark: "/solutions/wordmarks/holdings.png",
    hook: "Every asset, rental, and investment across jurisdictions in one view." },
  { key: "currency", name: "Currency Management Application", slug: "currency-management-application", img: "/solutions/currency.png", wordmark: "/solutions/wordmarks/currency.png",
    hook: "Physical, virtual, and crypto accounts in a single trustworthy ledger." },
  { key: "benefits", name: "Benefits Management Application", slug: "benefits-management-application", banner: "/solutions/benefits.png",
    hook: "Calm, accurate benefit schedules and professional disbursements." },
];

function StoreButton({ href, icon: Icon, label, testid }) {
  const cls = "inline-flex items-center gap-1.5 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift hover:bg-black/5 dark:hover:bg-white/10";
  return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}><Icon size={15} /> {label}</a>;
}

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1543710397-c21ee19e2b95?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.pexels.com/photos/20562278/pexels-photo-20562278.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.pexels.com/photos/22912025/pexels-photo-22912025.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1759281944287-9173dc7018e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

export function Home() {
  const b = useBranding();
  const t = useLanding();
  const { lang } = useApp();
  const [searchParams] = useSearchParams();
  const preview = searchParams.get("hero_preview") === "1";
  const previewData = useMemo(() => {
    if (!preview) return null;
    try { return JSON.parse(localStorage.getItem("hero_preview") || "null"); } catch { return null; }
  }, [preview]);
  const pickLang = (m) => (m && (m[lang] || m.ENG)) || "";
  const suiteName = (preview && previewData?.suite_name) || b?.suite_name || "Full Option Family Office";
  const heroTagline = (preview ? pickLang(previewData?.tagline) : "") || pickLang(b?.home_hero_tagline) || t("home_tagline");
  const heroLead = (preview ? pickLang(previewData?.lead) : "") || pickLang(b?.home_hero_lead) || t("home_lead");
  const heroImages = (b?.home_hero_images && b.home_hero_images.length) ? b.home_hero_images : HERO_IMAGES;
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 6000);
    return () => clearInterval(id);
  }, [heroImages.length]);
  useSeo("Full Option Family Office — Home",
    t("seo_home_desc"),
    { jsonLd: ORG_LD() });
  return (
    <Shell>
      <section className="relative overflow-hidden rounded-3xl shadow-xl" data-testid="home-hero">
        {heroImages.map((src, i) => (
          <img key={i} alt="enterprise" data-testid={`home-hero-image-${i}`} src={src}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${i === heroIdx ? "opacity-100" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/55 to-black/15" />
        <div className="relative px-8 py-20 text-white sm:px-14 sm:py-28">
          {preview && <span className="mb-4 inline-block rounded-full bg-[#F1C21B] px-3 py-1 text-xs font-semibold text-black" data-testid="home-preview-badge">Preview — unsaved hero text (not published)</span>}
          <img src="/brand-logo.png" alt="" className="mb-5 h-14 w-14 object-contain brightness-0 invert" />
          <h1 className="font-head text-4xl font-light leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="home-suite-name">{suiteName}</h1>
          {heroTagline && <p className="mt-3 max-w-2xl text-lg opacity-85" data-testid="home-tagline">{heroTagline}</p>}
          <p className="mt-4 max-w-2xl text-base opacity-80" data-testid="home-lead">{heroLead}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/solutions" data-testid="home-cta-explore"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-white/90">
              {t("home_explore")}
            </Link>
            <Link to="/login" data-testid="home-cta-signin"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              {t("sign_in")}
            </Link>
          </div>
          <p className="mt-8 text-sm opacity-75" data-testid="home-marks">
            · Familigi™ · BoxAlong™ · Currencida™ · SEBAA™ — {t("home_unified")}
          </p>
        </div>
      </section>
    </Shell>
  );
}

const MATRIX_APPS = [
  ["family_tree", "Familigi™", "family-tree-application"],
  ["holdings", "BoxAlong™", "holdings-management-application"],
  ["currency", "Currencida™", "currency-management-application"],
  ["benefits", "SEBAA™", "benefits-management-application"],
];
const ALL4 = ["family_tree", "holdings", "currency", "benefits"];
const MATRIX_ROWS = [
  { group: "fm_grp_best" },
  { k: "fm_consolidated_networth", a: ["holdings", "currency"], hi: true },
  { k: "fm_pe_analytics", a: ["holdings"], hi: true },
  { k: "fm_capital_calls", a: ["holdings"], hi: true },
  { k: "fm_exposure", a: ["holdings"], hi: true },
  { k: "fm_beneficiary_map", a: ["family_tree", "holdings"], hi: true },
  { k: "fm_export", a: ALL4, hi: true },
  { k: "fm_i18n", a: ALL4, hi: true },
  { k: "fm_ownership", a: ALL4, hi: true },
  { group: "fm_grp_common" },
  { k: "fm_data_viz", a: ALL4 }, { k: "fm_automation", a: ALL4 }, { k: "fm_data_analysis", a: ALL4 },
  { k: "fm_autocat", a: ALL4 }, { k: "fm_attachments", a: ALL4 }, { k: "fm_audit", a: ALL4 },
  { k: "fm_rbac", a: ALL4 }, { k: "fm_encryption", a: ALL4 }, { k: "fm_reminders", a: ALL4 },
  { k: "fm_collab_software", a: ALL4 }, { k: "fm_team_collab", a: ALL4 },
  { group: "fm_grp_byapp" },
  { k: "fm_presentation", a: ["family_tree"] }, { k: "fm_photo_sharing", a: ["family_tree"] },
  { k: "fm_tree_sharing", a: ["family_tree"] }, { k: "fm_orgmap", a: ["family_tree"] },
  { k: "fm_gedcom", a: ["family_tree"] },
  { k: "fm_asset_mgmt", a: ["holdings"] }, { k: "fm_appraisals", a: ["holdings"] },
  { k: "fm_treasury", a: ["holdings"] }, { k: "fm_investing", a: ["holdings", "currency"] },
  { k: "fm_budgeting", a: ["holdings", "currency"] },
  { k: "fm_crypto", a: ["currency"] }, { k: "fm_online_banking", a: ["currency"] },
  { k: "fm_financial_planning", a: ["currency"] },
  { k: "fm_benefits_mgmt", a: ["benefits"] }, { k: "fm_payroll", a: ["benefits"] },
  { k: "fm_savings", a: ["benefits"] }, { k: "fm_investing_mgmt", a: ["benefits"] },
];

const PROOF_KEYS = ["fm_proof_1", "fm_proof_2", "fm_proof_3", "fm_proof_4", "fm_proof_5"];

function FeatureMatrix({ t, onApp, onBuy, discount }) {
  return (
    <Card className="mt-12" data-testid="feature-matrix">
      <h2 className="font-head text-2xl font-bold" data-testid="feature-matrix-title">{t("fm_title")}</h2>
      <p className="mt-1 mb-5 text-sm opacity-70">{t("fm_intro")}</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-[var(--primary)]/30">
              <th className="py-2 text-start"> </th>
              {MATRIX_APPS.map(([k, name, to]) => (
                <th key={k} className="px-2 py-2 text-center">
                  <button onClick={() => onApp(to)} data-testid={`matrix-app-${to}`}
                    className="font-semibold text-[var(--primary)] hover:underline">{name}</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX_ROWS.map((row, i) => row.group ? (
              <tr key={i}>
                <td colSpan={5} className={`px-1 py-1.5 text-xs font-semibold uppercase tracking-wide ${row.group === "fm_grp_best" ? "bg-gradient-to-r from-[#F1C21B]/25 to-transparent text-[#8a6d00] dark:text-[#F1C21B]" : "bg-[var(--primary)]/5 text-[var(--primary)]"}`} data-testid={`matrix-group-${row.group}`}>{t(row.group)}</td>
              </tr>
            ) : (
              <tr key={i} className={`border-b border-black/5 dark:border-white/5 ${row.hi ? "bg-[#F1C21B]/5" : ""}`} data-testid={`matrix-row-${row.k}`}>
                <td className="py-2 pe-3">
                  {row.hi ? <span className="me-1 text-[#F1C21B]" aria-hidden>★</span> : null}
                  <span className={row.hi ? "font-semibold" : ""}>{t(row.k)}</span>
                </td>
                {MATRIX_APPS.map(([k]) => (
                  <td key={k} className="px-2 py-2 text-center">
                    {row.a.includes(k)
                      ? <Check size={16} className={`mx-auto ${row.hi ? "text-[#24A148]" : "text-[var(--primary)]"}`} data-testid={`matrix-tick-${row.k}-${k}`} />
                      : <span className="opacity-20">–</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 rounded-lg border-s-4 border-[var(--primary)] bg-[var(--primary)]/5 p-4" data-testid="matrix-key-takeaway">
        <span className="text-xs font-bold uppercase tracking-wide text-[var(--primary)]">{t("fm_kt_label")}</span>
        <p className="mt-1 text-sm">{t("fm_kt")}</p>
      </div>

      <div className="mt-5 rounded-lg border border-black/10 p-4 dark:border-white/10" data-testid="matrix-proof">
        <h3 className="font-head text-lg font-bold" data-testid="matrix-proof-title">{t("fm_proof_title")}</h3>
        <p className="mt-1 mb-3 text-sm opacity-70">{t("fm_proof_intro")}</p>
        <ul className="space-y-2">
          {PROOF_KEYS.map((pk) => (
            <li key={pk} className="flex items-start gap-2 text-sm" data-testid={`matrix-proof-${pk}`}>
              <Check size={16} className="mt-0.5 shrink-0 text-[#24A148]" />
              <span>{t(pk)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-black/10 dark:border-white/10 pt-4">
        <button onClick={onBuy} data-testid="matrix-buy-suite"
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-white lift hover:bg-[var(--primary-hover)]">
          <Layers size={16} /> {t("fm_cta").replace("{pct}", discount)}
        </button>
        <span className="text-xs opacity-60">{t("fm_cta_note")}</span>
      </div>
    </Card>
  );
}

const SOLUTION_PROGRAMS = {
  "suite": "FopFo™ Suite (all four applications)",
  "family-tree-application": "Familigi™ — Family Tree",
  "holdings-management-application": "BoxAlong™ — Holdings",
  "currency-management-application": "Currencida™ — Currency",
  "benefits-management-application": "SEBAA™ — Benefits",
};

function ReviewStars({ value, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 7 }, (_, i) => i + 1).map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value || 0) ? "fill-amber-400 text-amber-400" : "text-black/20 dark:text-white/20"} />
      ))}
    </span>
  );
}

const REVIEW_LANG_LABEL = PUBLIC_LANGS.reduce((m, l) => { m[l.code] = l.native; return m; }, { ENG: "English" });

// Renders the "Quick Feature Summary" block from the (already-translated) Solutions letter — the
// same cached paragraphs, just relocatable to the Reviews page. Program headers deep-link back to
// the matching Solutions card; program blocks carry their per-program review widget.
function QuickFeatureSummary({ letter, lang, t }) {
  if (!letter || letter.qfs_start == null || letter.qfs_start < 0) return null;
  const paras = letter.template.split("\n\n");
  const end = letter.qfs_end != null && letter.qfs_end >= 0 ? letter.qfs_end : paras.length;
  const renderRun = (key, tok) => {
    const s = letter.spans[Number(tok)];
    if (!s) return null;
    if (s.type === "link") {
      const to = s.to === "about" ? "/about" : `/solutions#sol-${s.to}`;
      return <Link key={key} to={to} className="font-semibold text-[var(--primary)] hover:underline">{s.v}</Link>;
    }
    if (s.type === "redemph") return <span key={key} className="font-bold italic underline text-[#DA1E28]">{s.v}</span>;
    return <em key={key} className="italic">{s.v}</em>;
  };
  const blocks = [];
  for (let pi = letter.qfs_start; pi < end; pi++) {
    const para = paras[pi];
    if (para == null) continue;
    const inner = para.split("\n").map((line, li) => {
      const arrow = line.startsWith("\u25ba ");
      const rest = arrow ? line.slice(2) : line;
      return (
        <React.Fragment key={li}>
          {li > 0 && <br />}
          {arrow && <span className="ms-4 me-1.5 text-[var(--primary)]">{"\u25ba"}</span>}
          {rest.split(/(\u27e6\d+\u27e7)/).map((seg, i) => {
            const m = seg.match(/^\u27e6(\d+)\u27e7$/);
            if (m) return renderRun(`${pi}-${li}-${i}`, m[1]);
            return <span key={i}>{renderTM(seg, `${pi}-${li}-${i}`)}</span>;
          })}
        </React.Fragment>
      );
    });
    const pk = para.includes("\u25ba")
      ? ([...para.matchAll(/\u27e6(\d+)\u27e7/g)].map((m) => letter.spans[Number(m[1])])
          .find((s) => s && s.type === "link" && SOLUTION_PROGRAMS[s.to]) || {}).to
      : null;
    if (pk && SOLUTION_PROGRAMS[pk]) {
      blocks.push(
        <div key={pi} className="grid items-start gap-4 sm:grid-cols-[1fr_18rem]" data-testid={`qfs-block-${pk}`}>
          <p>{inner}</p>
          <ProgramReview programKey={pk} programLabel={SOLUTION_PROGRAMS[pk]} defaultLang={lang || "ENG"} t={t} />
        </div>
      );
    } else {
      blocks.push(<p key={pi}>{inner}</p>);
    }
  }
  return <div className="max-w-3xl space-y-6 text-lg leading-relaxed opacity-90" data-testid="qfs-section">{blocks}</div>;
}

export function AllReviews() {
  const t = useLanding();
  const { lang } = useApp();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [letter, setLetter] = useState(null);
  const [prog, setProg] = useState(sp.get("program") || "ALL");
  const [flang, setFlang] = useState("ALL");
  const [minR, setMinR] = useState("ALL");
  const [sort, setSort] = useState("newest");
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);
  const [layout, setLayout] = useState("reviews_first");
  useSeo("Reviews — Full Option Family Office", "Read moderated 7-star reviews from users and verified customers across the FopFo™ suite of applications.");
  useEffect(() => { client.get("/public/reviews/all").then((r) => setData(r.data)).catch(() => setData({ items: [], count: 0, average: 0, langs: [], verified: 0, programs: {} })); }, []);
  useEffect(() => {
    let alive = true;
    client.get(`/site/solutions${lang && lang !== "ENG" ? `?lang=${lang}` : ""}`)
      .then((r) => { if (alive) setLetter(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [lang]);
  useEffect(() => { setPage(1); }, [prog, flang, minR, sort, pageSize]);

  const programs = data?.programs || {};
  const displayName = (raw) => (raw || "").trim() || t("rv_anon") || "Anonymous";
  const filtered = React.useMemo(() => {
    if (!data) return [];
    let rows = data.items.slice();
    if (prog !== "ALL") rows = rows.filter((r) => r.program === prog);
    if (flang !== "ALL") rows = rows.filter((r) => (r.lang || "ENG") === flang);
    if (minR !== "ALL") rows = rows.filter((r) => (r.rating || 0) >= Number(minR));
    if (sort === "highest") rows.sort((a, b) => b.rating - a.rating);
    else if (sort === "lowest") rows.sort((a, b) => a.rating - b.rating);
    else rows.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
    return rows;
  }, [data, prog, flang, minR, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const selClass = "rounded-md border border-black/15 bg-white/70 px-2.5 py-1.5 text-sm dark:border-white/15 dark:bg-black/30";

  const reviewsBlock = (
    <div data-testid="reviews-block">
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="all-reviews-filters">
        <select value={prog} onChange={(e) => setProg(e.target.value)} className={selClass} data-testid="filter-program">
          <option value="ALL">{t("rv_all_programs") || "All programs"}</option>
          {Object.entries(programs).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={flang} onChange={(e) => setFlang(e.target.value)} className={selClass} data-testid="filter-language">
          <option value="ALL">{t("rv_all_langs") || "All languages"}</option>
          {(data?.langs || []).map((c) => <option key={c} value={c}>{REVIEW_LANG_LABEL[c] || c}</option>)}
        </select>
        <select value={minR} onChange={(e) => setMinR(e.target.value)} className={selClass} data-testid="filter-rating">
          <option value="ALL">{t("rv_any_rating") || "Any rating"}</option>
          {[7, 6, 5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{`${n}+ ${t("rv_stars_word") || "stars"}`}</option>)}
        </select>
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={selClass} data-testid="filter-pagesize">
          {[12, 60, 120].map((n) => <option key={n} value={n}>{`${t("rv_show_word") || "Show"} ${n}`}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={selClass} data-testid="filter-sort">
          <option value="newest">{t("rv_sort_newest") || "Newest first"}</option>
          <option value="highest">{t("rv_sort_highest") || "Highest rated"}</option>
          <option value="lowest">{t("rv_sort_lowest") || "Lowest rated"}</option>
        </select>
        <select value={layout} onChange={(e) => setLayout(e.target.value)} className={selClass} data-testid="filter-layout">
          <option value="reviews_first">{t("rv_layout_reviews_first") || "Show reviews first"}</option>
          <option value="features_first">{t("rv_layout_features_first") || "Show feature summary first"}</option>
        </select>
        <span className="ms-auto text-sm opacity-60" data-testid="all-reviews-count">
          {filtered.length} {t("rv_reviews_word")}{totalPages > 1 ? ` · ${t("rv_page_word")} ${curPage}/${totalPages}` : ""}
        </span>
      </div>

      {data && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-black/15 p-10 text-center dark:border-white/15" data-testid="all-reviews-empty">
          <MessageSquare className="mx-auto mb-2 opacity-40" size={28} />
          <p className="opacity-60">{t("rv_none_match") || "No reviews match these filters yet."}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="all-reviews-grid">
        {pageRows.map((r) => (
          <div key={r.id} className="flex flex-col rounded-xl border border-black/10 bg-white/70 p-4 dark:border-white/10 dark:bg-black/20" data-testid={`all-review-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <ReviewStars value={r.rating} />
              <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] opacity-70 dark:bg-white/10">{REVIEW_LANG_LABEL[r.lang] || r.lang}</span>
            </div>
            <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)]">{r.program_label || programs[r.program] || r.program}</div>
            <p className="mt-2 flex-1 text-sm opacity-90">{r.comment}</p>
            {r.comment_en && r.lang !== "ENG" && <p className="mt-2 border-t border-black/5 pt-2 text-xs italic opacity-70 dark:border-white/5">{t("rv_en_label") || "EN"}: {r.comment_en}</p>}
            <div className="mt-3 flex items-center gap-2 text-xs opacity-60">
              <span>— {displayName(r.name)}</span>
              {r.verified && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 font-medium text-emerald-700 dark:text-emerald-400" data-testid={`all-review-verified-${r.id}`}><BadgeCheck size={11} /> {t("rv_verified") || "Verified"}</span>}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-1.5" data-testid="all-reviews-pagination">
          <button onClick={() => setPage(Math.max(1, curPage - 1))} disabled={curPage === 1} className="rounded-md border border-black/15 px-2.5 py-1 text-sm disabled:opacity-40 dark:border-white/15" data-testid="page-prev">‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button key={n} onClick={() => setPage(n)} data-testid={`page-${n}`}
              className={`min-w-[2rem] rounded-md px-2.5 py-1 text-sm ${n === curPage ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>{n}</button>
          ))}
          <button onClick={() => setPage(Math.min(totalPages, curPage + 1))} disabled={curPage === totalPages} className="rounded-md border border-black/15 px-2.5 py-1 text-sm disabled:opacity-40 dark:border-white/15" data-testid="page-next">›</button>
        </div>
      )}
    </div>
  );

  const featuresBlock = (
    <div data-testid="features-block">
      <QuickFeatureSummary letter={letter} lang={lang} t={t} />
    </div>
  );

  return (
    <Shell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3" data-testid="all-reviews-header">
        <div>
          <h1 className="font-head text-3xl sm:text-4xl">{t("nav_reviews") || "Reviews"}</h1>
          <p className="mt-1 text-sm opacity-70">{t("rv_page_subtitle")}</p>
        </div>
        <Link to="/solutions" className="text-sm font-medium text-[var(--primary)] hover:underline" data-testid="all-reviews-back">← {t("rv_back_to_solutions") || "Back to Solutions"}</Link>
      </div>

      {data && data.count > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3" data-testid="all-reviews-summary">
          <ReviewStars value={data.average} size={18} />
          <span className="text-lg font-semibold">{data.average}/7</span>
          <span className="opacity-70">{t("rv_across")} {data.count} {t("rv_reviews_word")}{data.verified > 0 ? ` · ${data.verified} ${t("rv_verified_from")}` : ""}</span>
        </div>
      )}

      {!data && <p className="opacity-60" data-testid="all-reviews-loading">{t("rv_loading") || "Loading reviews…"}</p>}

      <div className="space-y-10">
        {layout === "features_first" ? <>{featuresBlock}{reviewsBlock}</> : <>{reviewsBlock}{featuresBlock}</>}
      </div>
    </Shell>
  );
}

function SupportTiers({ t }) {
  const [tiers, setTiers] = useState(null);
  useEffect(() => { client.get("/public/support-tiers").then((r) => setTiers(r.data.tiers)).catch(() => setTiers([])); }, []);
  // Corner half-moon accent, tuned to read as the metal each tier is named for.
  const CORNER = {
    diamond: "radial-gradient(circle at 30% 30%, #4589FF, #0F62FE 55%, #0043CE)",
    platinum: "linear-gradient(135deg, #ffffff, #d9dde3 45%, #a7aeb8)",
    rhodium: "linear-gradient(135deg, #c4c8ce, #8b9199 45%, #565b63)",
    osmium: "linear-gradient(135deg, #3a4150, #15171d 60%, #050507)",
  };
  const BEST = { diamond: "st_diamond_for", platinum: "st_platinum_for", rhodium: "st_rhodium_for", osmium: "st_osmium_for" };
  return (
    <Card id="support-tiers" className="mt-12 scroll-mt-24" data-testid="support-tiers">
      <h2 className="font-head text-2xl font-bold" data-testid="support-tiers-title">{t("st_title")}</h2>
      <p className="mt-2 text-sm opacity-70 max-w-2xl">{t("st_intro")}</p>
      <div className="mt-3 inline-flex items-start gap-2 rounded-lg border-s-4 border-[#24A148] bg-[#24A148]/8 p-3 text-sm" data-testid="support-tiers-no-aum">
        <Check size={18} className="mt-0.5 shrink-0 text-[#24A148]" /><span className="font-medium">{t("st_no_aum")}</span>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(tiers || []).map((tier) => (
          <div key={tier.key} className="relative overflow-hidden rounded-xl border border-black/10 bg-white/70 p-4 lift dark:border-white/10 dark:bg-white/5" data-testid={`tier-card-${tier.key}`}>
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-80 shadow-inner" style={{ background: CORNER[tier.key] }} data-testid={`tier-corner-${tier.key}`} />
            <div className="text-xs font-semibold uppercase tracking-wide" data-testid={`tier-name-${tier.key}`}>{tier.name}</div>
            <div className="mt-1 text-3xl font-bold tabular-nums">${tier.monthly_usd.toFixed(2)}<span className="text-xs font-normal opacity-50">/mo</span></div>
            <ul className="mt-3 space-y-1.5 text-sm">
              <li className="flex justify-between"><span className="opacity-60">{t("st_col_parses")}</span><span className="font-semibold" data-testid={`tier-parses-${tier.key}`}>{tier.parses > 0 ? tier.parses : t("st_addon")}</span></li>
              <li className="flex justify-between"><span className="opacity-60">{t("st_col_questions")}</span><span className="font-semibold">{tier.questions}</span></li>
              <li className="flex justify-between"><span className="opacity-60">{t("st_col_sla")}</span><span className="font-semibold">{tier.sla_hours}h</span></li>
              <li className="flex justify-between"><span className="opacity-60">{t("st_col_portal")}</span><span className="font-semibold text-end" data-testid={`tier-portal-${tier.key}`}>{tier.order >= 4 ? t("st_portal_osmium") : (tier.order >= 2 ? t("st_portal_custom") : t("st_portal_base"))}</span></li>
            </ul>
            <div className="mt-3 border-t border-black/5 pt-2 text-xs opacity-70 dark:border-white/10">{t(BEST[tier.key])}</div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs opacity-60">{t("st_included")}</p>
      <p className="mt-1 text-xs opacity-50">{t("st_footnote")}</p>
    </Card>
  );
}


export function Solutions() {
  const b = useBranding();
  const t = useLanding();
  const { lang } = useApp();
  const nav = useNavigate();
  const openWatch = (appKey = "") => nav(`/watch-demo${appKey ? `?app=${appKey}` : ""}`);
  const storeLinks = b?.store_links || {};
  const [pricing, setPricing] = useState(null);
  useEffect(() => { client.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const cur = (pricing?.currency || "usd").toUpperCase();
  const priceLabel = (key) => {
    const p = pricing?.apps?.[key];
    if (!p) return null;
    return `${cur} ${p.total.toFixed(2)} · per app`;
  };
  const [reviewSummary, setReviewSummary] = useState(null);
  useEffect(() => { client.get("/public/reviews/summary").then((r) => setReviewSummary(r.data?.overall || null)).catch(() => {}); }, []);
  const aggregateLd = reviewSummary && reviewSummary.count > 0 ? {
    "@context": "https://schema.org", "@type": "Product",
    name: "Full Option Family Office (FopFo) Suite",
    description: "A suite of five interworking family-office applications: Family Tree, Holdings, Currency, Benefits and the Master Enabler.",
    brand: { "@type": "Brand", name: "FopFo" },
    aggregateRating: {
      "@type": "AggregateRating", ratingValue: reviewSummary.average,
      bestRating: 7, worstRating: 1, ratingCount: reviewSummary.count,
    },
  } : null;
  useSeo("Solutions — Full Option Family Office", t("seo_solutions_desc"),
    { jsonLd: aggregateLd ? [ORG_LD(), aggregateLd] : ORG_LD() });
  const scrollToCard = (slug) => {
    const el = document.getElementById(`sol-${slug}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[var(--primary)]");
    setTimeout(() => el.classList.remove("ring-2", "ring-[var(--primary)]"), 1600);
  };
  const location = useLocation();
  useEffect(() => {
    const id = (location.hash || "").replace(/^#sol-/, "");
    if (id) setTimeout(() => scrollToCard(id), 250);
  }, [location.hash]);
  const [sf, setSf] = useState(null);
  const [testMode, setTestMode] = useState(false);
  useEffect(() => { client.get("/storefront/pricing").then((r) => setSf(r.data)).catch(() => {}); }, []);
  useEffect(() => { client.get("/payments/mode").then((r) => setTestMode(!!r.data.test_mode)).catch(() => {}); }, []);
  const [buy, setBuy] = useState(null);   // { purchase: "app"|"suite", appKey, appName }
  const [bform, setBform] = useState({ name: "", email: "", password: "", phone: "", newsletter: false, tech_updates: false });
  const [bbusy, setBbusy] = useState(false);
  const [berr, setBerr] = useState("");
  const openBuy = (purchase, appKey = "", appName = "") => { setBerr(""); setBform({ name: "", email: "", password: "", phone: "", newsletter: false, tech_updates: false }); setBuy({ purchase, appKey, appName }); };
  const submitBuy = async () => {
    setBbusy(true); setBerr("");
    try {
      const { data } = await client.post("/storefront/signup-checkout", {
        ...bform, purchase: buy.purchase, app_type: buy.appKey || "", origin_url: window.location.origin });
      if (!data?.url || !data?.token) throw new Error("no-session");
      localStorage.setItem("suite_token", data.token);
      window.location.href = data.url;
    } catch (e) {
      setBerr((e?.message === "no-session" ? "Could not start checkout. Please try again." : apiErr(e)) || "Could not start checkout. Please try again.");
      setBbusy(false);
    }
  };
  const [letter, setLetter] = useState(null);
  useEffect(() => {
    let alive = true;
    client.get(`/site/solutions${lang && lang !== "ENG" ? `?lang=${lang}` : ""}`)
      .then((r) => { if (alive) setLetter(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [lang]);
  const renderRun = (key, tok, spans) => {
    const s = spans[Number(tok)];
    if (!s) return null;
    if (s.type === "link") {
      if (s.to === "about") return <Link key={key} to="/about" data-testid="solution-link-about" className="font-semibold text-[var(--primary)] hover:underline">{s.v}</Link>;
      return <a key={key} href={`#sol-${s.to}`} onClick={(e) => { e.preventDefault(); scrollToCard(s.to); }} data-testid={`solution-link-${s.to}`} className="cursor-pointer font-semibold text-[var(--primary)] hover:underline">{s.v}</a>;
    }
    if (s.type === "redemph") return <span key={key} className="font-bold italic underline text-[#DA1E28]">{s.v}</span>;
    return <em key={key} className="italic">{s.v}</em>;
  };
  return (
    <Shell>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head text-4xl" data-testid="solutions-title">{t("solutions_title")}</h1>
        <Link to="/reviews" className="text-sm font-medium text-[var(--primary)] hover:underline" data-testid="solutions-to-reviews">{t("rv_on_to_reviews") || "On to Reviews"} →</Link>
      </div>
      <button onClick={() => nav("/watch-demo?app=suite&voice=1")} data-testid="solutions-play-tour"
        className="group mb-6 inline-flex items-center gap-3 rounded-full bg-[var(--primary)] px-6 py-3 text-white shadow-lg transition hover:shadow-xl hover:brightness-110">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 transition group-hover:bg-white/30"><Play size={18} className="ms-0.5 fill-current" /></span>
        <span className="text-start leading-tight">
          <span className="block text-sm font-semibold">{t("play_tour")}</span>
          <span className="flex items-center gap-1 text-[11px] opacity-80"><Volume2 size={11} /> {t("play_tour_sub")}</span>
        </span>
      </button>
      {reviewSummary && reviewSummary.count > 0 && (
        <div className="mb-6 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-sm" data-testid="reviews-summary-strip">
          <span className="inline-flex items-center gap-0.5">
            {Array.from({ length: 7 }, (_, i) => i + 1).map((n) => (
              <Star key={n} size={14} className={n <= Math.round(reviewSummary.average) ? "fill-amber-400 text-amber-400" : "text-black/20 dark:text-white/20"} />
            ))}
          </span>
          <span className="font-semibold">{reviewSummary.average}/7</span>
          <span className="opacity-70">{t("rv_across")} {reviewSummary.count} {t("rv_reviews_word")}{reviewSummary.verified > 0 ? ` · ${reviewSummary.verified} ${t("rv_verified_from")}` : ""}</span>
          <Link to="/reviews" className="ms-1 font-medium text-[var(--primary)] hover:underline" data-testid="solutions-see-all-reviews">{t("rv_see_all") || "See all reviews"} →</Link>
        </div>
      )}
      <div className="max-w-3xl space-y-6 text-lg leading-relaxed opacity-90" data-testid="solutions-content">
        {!letter && <p className="opacity-60" data-testid="solutions-loading">{t("rv_loading")}</p>}
        {letter && letter.template.split("\n\n").map((para, pi) => {
          // The Quick Feature Summary now lives on the /reviews page; skip it here.
          if (letter.qfs_start >= 0 && pi >= letter.qfs_start &&
              pi < (letter.qfs_end >= 0 ? letter.qfs_end : Infinity)) return null;
          const inner = para.split("\n").map((line, li) => {
              const diamond = line.startsWith("◆ ");
              const arrow = line.startsWith("► ");
              const rest = (diamond || arrow) ? line.slice(2) : line;
              return (
                <React.Fragment key={li}>
                  {li > 0 && <br />}
                  {diamond && <span className="text-[var(--primary)] me-1">◆</span>}
                  {arrow && <span className="ms-4 me-1.5 text-[var(--primary)]">►</span>}
                  {rest.split(/(⟦\d+⟧)/).map((seg, i) => {
                    const m = seg.match(/^⟦(\d+)⟧$/);
                    if (m) return renderRun(`${pi}-${li}-${i}`, m[1], letter.spans);
                    return <span key={i}>{renderTM(seg, `${pi}-${li}-${i}`)}</span>;
                  })}
                </React.Fragment>
              );
            });
          // In the Quick Feature Summary, attach a rating + review widget to the right of each program block.
          const pk = para.includes("►")
            ? ([...para.matchAll(/⟦(\d+)⟧/g)].map((m) => letter.spans[Number(m[1])])
                .find((s) => s && s.type === "link" && SOLUTION_PROGRAMS[s.to]) || {}).to
            : null;
          if (pk && SOLUTION_PROGRAMS[pk]) {
            return (
              <div key={pi} className="grid items-start gap-4 sm:grid-cols-[1fr_18rem]" data-testid={`qfs-block-${pk}`}>
                <p>{inner}</p>
                <ProgramReview programKey={pk} programLabel={SOLUTION_PROGRAMS[pk]} defaultLang={lang || "ENG"} t={t} />
              </div>
            );
          }
          if (letter.infographic_after === pi) {
            return (
              <React.Fragment key={pi}>
                <p>{inner}</p>
                <SolutionsInfographic t={t} onNavigate={scrollToCard} />
              </React.Fragment>
            );
          }
          return <p key={pi}>{inner}</p>;
        })}
      </div>
      <FeatureMatrix t={t} onApp={scrollToCard} onBuy={() => openBuy("suite")} discount={sf ? sf.suite_discount_pct : 10} />
      <SupportTiers t={t} />
      <div className="mt-12 grid gap-5 sm:grid-cols-2" data-testid="solutions-cards">
        <Card id="sol-suite" className="h-full lift scroll-mt-24 sm:col-span-2 border-2 border-[var(--primary)]/40" data-testid="solution-card-suite">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-head text-xl font-bold text-[var(--primary)]">FopFo™ — {t("sc_full_suite")}</span>
            <Badge color="success">{t("sc_discount_badge").replace("{pct}", sf ? sf.suite_discount_pct : 10)}</Badge>
          </div>
          <p className="mt-1 text-sm opacity-70">{t("sc_suite_desc")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="text-xl font-semibold" data-testid="solution-price-suite">{sf ? `USD ${sf.suite_price.toFixed(2)}` : "…"}</div>
            {sf && <div className="text-sm opacity-60 line-through">USD {(sf.base * 4).toFixed(2)}</div>}
            <button onClick={() => openBuy("suite")} data-testid="solution-suite-buy"
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-medium text-white lift hover:bg-[var(--primary-hover)]">
              <Layers size={16} /> {t("get_full_suite")}
            </button>
            <button onClick={() => openWatch("suite")} data-testid="solution-watch-suite"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/15 dark:border-white/20 bg-white px-4 py-2 text-sm font-medium text-[var(--primary)] lift hover:bg-black/5">
              <Play size={16} /> {t("watch_demo")}
            </button>
            <span data-testid="solution-narrated-suite" className="inline-flex items-center gap-1 rounded-full border border-[var(--primary)]/30 bg-[var(--primary)]/10 px-2 py-1 text-[11px] font-medium text-[var(--primary)]">
              <Volume2 size={12} /> {t("demo_narrated")}
            </span>
          </div>
          <div className="mt-1 text-[11px] opacity-60">{t("sc_taxes")}</div>
          <div className="mt-1 text-[11px] opacity-70" data-testid="suite-service-note">{t("sc_service")}</div>
        </Card>
        <Card id="sol-please-note" className="h-full scroll-mt-24 sm:col-span-2 border border-[var(--primary)]/30 bg-[var(--primary)]/[0.04]" data-testid="solution-card-please-note">
          <div className="flex items-center gap-2">
            <Info size={18} className="text-[var(--primary)]" />
            <span className="font-head text-lg font-bold text-[var(--primary)]">{t("pn_title")}</span>
          </div>
          <p className="mt-2 text-sm opacity-80"><RichText text={t("pn_intro")} /></p>
          <ul className="mt-2 space-y-1.5 text-sm opacity-85">
            <li className="flex gap-2"><span className="text-[var(--primary)]">◆</span><span><RichText text={t("pn_b1")} /></span></li>
            <li className="flex gap-2"><span className="text-[var(--primary)]">◆</span><span><RichText text={t("pn_b2")} /></span></li>
            <li className="flex gap-2"><span className="text-[var(--primary)]">◆</span><span><RichText text={t("pn_b3")} /></span></li>
          </ul>
          <p className="mt-2 text-[11px] opacity-60" data-testid="please-note-tax">{t("pn_foot")}</p>
        </Card>
        {PRODUCT_CARDS.map((c) => (
          <Card key={c.slug} id={`sol-${c.slug}`} className="h-full lift scroll-mt-24 transition-shadow duration-500" data-testid={`solution-card-${c.slug}`}>
            {c.banner ? (
              <div className="flex flex-col items-center text-center">
                <img src={c.banner} alt={`${c.name} banner`} data-testid={`solution-banner-${c.slug}`} className="h-48 w-auto object-contain" />
                <h3 className="mt-2 font-head text-lg">{t(`name_${c.key}`) || c.name}</h3>
                <p className="mt-1 text-sm opacity-70">{t(`hook_${c.key}`) || c.hook}</p>
                <Link to={`/blog/${c.slug}`} data-testid={`solution-card-link-${c.slug}`}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline">
                  {t("card_learn_more")} <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-black/5 dark:bg-white/5 p-1.5">
                    <img src={c.img} alt={`${c.name} logo`} data-testid={`solution-logo-${c.slug}`} className="h-full w-full object-contain" />
                  </div>
                  {c.wordmark && <img src={c.wordmark} alt={`${c.name} wordmark`} data-testid={`solution-wordmark-${c.slug}`} className="h-8 w-24 object-contain" />}
                </div>
                <div className="flex-1">
                  <h3 className="font-head text-lg">{t(`name_${c.key}`) || c.name}</h3>
                  <p className="mt-1 text-sm opacity-70">{t(`hook_${c.key}`) || c.hook}</p>
                  <Link to={`/blog/${c.slug}`} data-testid={`solution-card-link-${c.slug}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline">
                    {t("card_learn_more")} <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            )}
            <div className="mt-4 border-t border-black/10 dark:border-white/10 pt-3">
              <div className="text-sm font-semibold" data-testid={`solution-price-${c.slug}`}>
                {sf ? `USD ${sf.single_price.toFixed(2)} · ${t("sc_one_time")}` : "…"}
                {sf && <span className="ms-1 text-[11px] font-normal opacity-60">({sf.single_discount_pct}% {t("sc_web_discount")})</span>}
              </div>
              {sf && <div className="mt-0.5 text-[11px] opacity-60" data-testid={`solution-store-price-${c.slug}`}>{t("sc_store_price").replace("{amount}", sf.base.toFixed(2))}</div>}
              <div className="mt-0.5 text-[11px] opacity-60" data-testid={`solution-tax-note-${c.slug}`}>{t("sc_taxes")}</div>
              <div className="text-[11px] opacity-70" data-testid={`solution-service-note-${c.slug}`}>{t("sc_service")}</div>
              <div className="mt-2 flex items-stretch gap-1.5">
                <button onClick={() => openBuy("app", c.key, c.name)} data-testid={`solution-buy-${c.slug}`}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 rounded-md bg-[var(--primary)] px-2 py-1.5 text-[11px] font-medium text-white lift hover:bg-[var(--primary-hover)]">
                  <ShoppingBag size={13} className="shrink-0" /> <span className="truncate">{t("buy_application")}</span>
                </button>
                <button onClick={() => openWatch(c.key)} data-testid={`solution-watch-${c.slug}`}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 rounded-md border border-black/15 dark:border-white/20 bg-white px-2 py-1.5 text-[11px] font-medium text-[var(--primary)] lift hover:bg-black/5">
                  <Play size={13} className="shrink-0" /> <span className="truncate">{t("watch_demo")}</span>
                </button>
                <button onClick={() => openBuy("suite")} data-testid={`solution-suite-${c.slug}`}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 rounded-md bg-[var(--primary)] px-2 py-1.5 text-[11px] font-medium text-white lift hover:bg-[var(--primary-hover)]">
                  <Layers size={13} className="shrink-0" /> <span className="truncate">{t("get_full_suite")}</span>
                </button>
              </div>
              <div className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-[var(--primary)]" data-testid={`solution-narrated-${c.slug}`}>
                <Volume2 size={11} className="shrink-0" /> {t("demo_narrated_note")}
              </div>
              {(storeLinks[c.key]?.app_store || storeLinks[c.key]?.play_store) && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid={`solution-stores-${c.slug}`}>
                  {storeLinks[c.key]?.app_store && <StoreButton href={storeLinks[c.key].app_store} icon={Apple} label="App Store" testid={`solution-appstore-${c.slug}`} />}
                  {storeLinks[c.key]?.play_store && <StoreButton href={storeLinks[c.key].play_store} icon={Play} label="Google Play" testid={`solution-playstore-${c.slug}`} />}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
      <div className="mt-8"><Link to="/contact?pay=start"><Button data-testid="solutions-pay">{t("make_payment")}</Button></Link></div>

      <Modal open={!!buy} onClose={() => !bbusy && setBuy(null)} title={buy?.purchase === "suite" ? "Get the Full Suite" : `Buy — ${buy?.appName || "Application"}`} testid="buy-modal">
        <div className="space-y-3">
          {testMode && <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400" data-testid="buy-test-mode">TEST MODE — no real charge. Use Stripe test card 4242 4242 4242 4242.</div>}
          <div className="rounded-md bg-black/[0.03] dark:bg-white/[0.06] p-3 text-sm" data-testid="buy-summary">
            {buy?.purchase === "suite"
              ? <>Full Option Family Office — all 4 applications. <b>USD {sf ? sf.suite_price.toFixed(2) : "…"}</b> <span className="opacity-60">({sf ? sf.suite_discount_pct : 10}% web discount)</span></>
              : <><b>{buy?.appName}</b> — one-time unlock. <b>USD {sf ? sf.single_price.toFixed(2) : "…"}</b> <span className="opacity-60">({sf ? sf.single_discount_pct : 5}% web discount)</span></>}
            <div className="mt-1 text-[11px] opacity-60">Applicable tax is added at checkout. You'll create your account now and receive your 20-character activation code after payment.</div>
            <div className="mt-2 rounded bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400" data-testid="buy-service-note">Then <b>USD 90.00/yr per account</b> (customer support, updates, and newsletter), due immediately on purchase — tax at checkout.</div>
          </div>
          <Input label="Full name" value={bform.name} onChange={(e) => setBform({ ...bform, name: e.target.value })} data-testid="buy-name" />
          <Input label="Email (this becomes your sign-in)" type="email" value={bform.email} onChange={(e) => setBform({ ...bform, email: e.target.value })} data-testid="buy-email" />
          <Input label="Mobile number (for secure recovery)" value={bform.phone} onChange={(e) => setBform({ ...bform, phone: e.target.value })} data-testid="buy-phone" />
          <Input label="Create a password (min 6 characters)" type="password" value={bform.password} onChange={(e) => setBform({ ...bform, password: e.target.value })} data-testid="buy-password" />
          <div className="space-y-1.5 rounded-md border border-black/10 dark:border-white/10 p-2.5">
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5" checked={bform.newsletter} onChange={(e) => setBform({ ...bform, newsletter: e.target.checked })} data-testid="buy-newsletter" /> <span>Email me the <b>FIDUX™ Newsletter</b>.</span></label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5" checked={bform.tech_updates} onChange={(e) => setBform({ ...bform, tech_updates: e.target.checked })} data-testid="buy-tech" /> <span>Email me <b>product &amp; technical updates</b>.</span></label>
            <div className="text-[11px] opacity-55">Optional — you can change these anytime in your account.</div>
          </div>
          {berr && <div className="text-sm text-[#DA1E28]" data-testid="buy-error">{berr}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setBuy(null)} disabled={bbusy}>Cancel</Button>
            <Button onClick={submitBuy} data-testid="buy-submit"
              disabled={bbusy || !bform.name.trim() || !bform.email.trim() || !bform.phone.trim() || bform.password.length < 6}>
              {bbusy ? <Loader2 size={15} className="animate-spin" /> : <ShoppingBag size={15} />} Continue to payment
            </Button>
          </div>
          <p className="text-center text-xs opacity-60">Already have an account? <Link to="/login" className="text-[var(--primary)] hover:underline">Sign in</Link> and buy from inside your account.</p>
        </div>
      </Modal>
    </Shell>
  );
}
